import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  ALL_PUMP_CHANNELS,
  findChannelByValue,
  toWholeNumberReading,
  type PumpChannel,
} from "../utils/pumpChannels.ts";
import {
  parseColumnarToBuffers,
  minMaxDownsample,
  sliceBuffer,
  DEFAULT_MAX_POINTS,
  type ChannelBuffer,
  type FleetBuffers,
} from "../utils/pumpSeries.ts";
import {
  channelAxisGridOptions,
  emptyChartGridAxis,
} from "../utils/chartGrid.ts";

Highcharts.setOptions({
  time: {
    useUTC: false,
  },
} as any);

interface AxisValueEditState {
  axis: Highcharts.Axis;
  axisTitle: string;
  clickedValue: number;
  ratioFromBottom: number;
  currentMin: number;
  currentMax: number;
  error?: string;
}

interface PumpData {
  id: number;
  time_stamp: number;
  pump_name: string;
  [key: string]: any;
}

type AxisName = "left1" | "left2" | "right1" | "right2";

type PopoutAxisSettings = Record<
  AxisName,
  {
    channels: string[];
    min?: number;
    max?: number;
  }
>;

type PopoutAxisExtremes = Record<
  AxisName,
  {
    min?: number;
    max?: number;
  }
>;

interface PumpChartHandoff {
  key: string;
  buffers?: FleetBuffers;
  rows?: PumpData[];
  axisSettings?: PopoutAxisSettings;
  axisAutoScale?: Record<AxisName, boolean>;
  axisExtremes?: PopoutAxisExtremes;
  channelOptions?: PumpChannel[];
}

const DEFAULT_AXIS_SETTINGS: PopoutAxisSettings = {
  left1: { channels: ["rpm"], min: undefined, max: undefined },
  left2: { channels: ["discharge_pressure"], min: undefined, max: undefined },
  right1: { channels: ["engine_temp"], min: undefined, max: undefined },
  right2: { channels: [], min: undefined, max: undefined },
};

interface PopoutTabTemplate {
  label: string;
  // Requested in priority order. Every candidate of every tab is fetched up
  // front in one columnar load, and each tab then binds to the first candidate
  // that actually returned samples — so a fleet reporting only a legacy column
  // still charts. Raw `dbo.pumps` column names come FIRST because live
  // streaming rows carry the raw keys, not the backend's aliased channel names;
  // binding to the raw key keeps history and live appends in the same buffer.
  candidates: string[];
}

const POWER_END_TEMPERATURE_CHANNELS: PumpChannel[] = [
  {
    value: "powerend_oil_temperature",
    label: "Power End: Oil Temperature (°F)",
  },
  {
    value: "powerend_oil_inlet_temperature",
    label: "Power End: Oil Inlet Temperature (°F)",
  },
  {
    value: "powerend_oil_outlet_temperature",
    label: "Power End: Oil Outlet Temperature (°F)",
  },
];

const POWER_END_TEMPERATURE_TRIGGER_VALUES = new Set([
  "powerend_oil_temperature",
  "powerend_oil_inlet_temperature",
  "powerend_oil_outlet_temperature",
  "pe_temp",
  "pe_outlet_temp",
]);

const isPowerEndTemperatureValue = (value: string) =>
  POWER_END_TEMPERATURE_TRIGGER_VALUES.has(String(value || "").trim());

const powerEndTemperatureBuffer = (
  channelMap: Map<string, ChannelBuffer>,
  channelValue: string,
): ChannelBuffer | undefined => {
  if (channelValue === "powerend_oil_temperature") {
    return (
      channelMap.get("powerend_oil_temperature") || channelMap.get("pe_temp")
    );
  }

  if (channelValue === "powerend_oil_inlet_temperature") {
    return (
      channelMap.get("powerend_oil_inlet_temperature") ||
      channelMap.get("powerend_oil_temperature") ||
      channelMap.get("pe_temp")
    );
  }

  if (channelValue === "powerend_oil_outlet_temperature") {
    return (
      channelMap.get("powerend_oil_outlet_temperature") ||
      channelMap.get("pe_outlet_temp")
    );
  }

  return channelMap.get(channelValue);
};

const PE_OIL_TEMP_TAB: PopoutTabTemplate = {
  label: "PE Oil Temp",
  candidates: [
    "powerend_oil_temperature",
    "pe_temp",
    "powerend_oil_inlet_temperature",
    "powerend_oil_outlet_temperature",
  ],
};

// The JDD-style channel strip, in the order requested for diesel fleets.
const SHARED_TAB_TEMPLATE: PopoutTabTemplate[] = [
  { label: "Displacement", candidates: ["inst_fuel_disp", "df_displacement"] },
  { label: "Engine RPM", candidates: ["rpm"] },
  { label: "Engine Oil Pres", candidates: ["engine_oil_pressure"] },
  {
    label: "Engine Oil Temp",
    candidates: ["engine_oil_temp", "engine_oil_temperature"],
  },
  {
    label: "Trans Oil Pres",
    candidates: ["trans_pressure", "trans_oil_pressure"],
  },
  {
    label: "Trans Oil Temp",
    candidates: ["trans_temp", "trans_oil_temperature"],
  },
  {
    label: "PE Oil Pres",
    candidates: ["pe_pressure", "powerend_oil_pressure"],
  },
  PE_OIL_TEMP_TAB,
];

const DIESEL_TAB_TEMPLATE: PopoutTabTemplate[] = SHARED_TAB_TEMPLATE;

// Electric fleets lead with PE Oil Temp; the trailing duplicate collapses in the
// dedupe that `channelTabs` applies.
const ELECTRIC_TAB_TEMPLATE: PopoutTabTemplate[] = [
  PE_OIL_TEMP_TAB,
  ...SHARED_TAB_TEMPLATE,
];

// The chart is sized from its flex container, never from the viewport, so the
// page itself never scrolls. Kept identical to the Pump Data page legend
// (Fleet.tsx) so both views read the same; alignColumns:false lets items pack
// across the full width instead of snapping into a narrow column grid.
const POPOUT_LEGEND: Highcharts.LegendOptions = {
  enabled: true,
  align: "center",
  verticalAlign: "bottom",
  layout: "horizontal",
  alignColumns: false,
  floating: false,
  maxHeight: 96,
  x: 0,
  y: 0,
  itemStyle: {
    color: "#e5e5e5",
    fontSize: "10px",
    fontWeight: "400",
    textOverflow: "ellipsis",
  },
  itemHoverStyle: { color: "#ffffff" },
  itemHiddenStyle: { color: "#666666" },
  navigation: {
    enabled: true,
    activeColor: "#92d13d",
    inactiveColor: "#6b7280",
    style: { color: "#e5e5e5", fontSize: "10px" },
  },
  itemDistance: 14,
  itemMarginTop: 2,
  itemMarginBottom: 2,
  padding: 8,
  backgroundColor: "rgba(48, 51, 53, 0.9)",
  borderRadius: 4,
  borderColor: "#3d4247",
  borderWidth: 1,
};

// Room for the y-axis stacks: left2 sits at offset 55, right2 at offset 70.
const CHART_MARGIN_LEFT = 120;
const CHART_MARGIN_RIGHT = 132;

const escapeTooltipText = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Nearest point to `x` in a series, by binary search over the rendered points.
// Highcharts' own shared tooltip pairs series up by exact x, but every series
// here is min/max downsampled from its own buffer (see minMaxDownsample), so
// each one keeps a different subset of timestamps and most of them would drop
// out of the tooltip. Matching on proximity instead keeps every series in.
const nearestPointAtX = (series: Highcharts.Series, x: number) => {
  const points = series.points;
  if (!points || points.length === 0) return null;

  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (points[mid].x < x) low = mid + 1;
    else high = mid;
  }

  let best: Highcharts.Point | null = null;
  let bestDistance = Infinity;
  // The binary search lands next to the answer, not necessarily on it.
  for (const candidate of [points[low - 1], points[low], points[low + 1]]) {
    if (!candidate || candidate.y === null || candidate.y === undefined) {
      continue;
    }
    const distance = Math.abs(candidate.x - x);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  return best;
};

// Lists every legend entry's value at the hovered time, not just the series
// under the cursor.
const ALL_SERIES_TOOLTIP: Highcharts.TooltipOptions = {
  shared: true,
  // Rendered into the body so a long list is never clipped by the plot area.
  outside: true,
  useHTML: true,
  followPointer: false,
  backgroundColor: "rgba(32, 36, 39, 0.97)",
  borderColor: "#5c646b",
  borderWidth: 1,
  borderRadius: 8,
  shadow: false,
  style: { color: "#e9edf1", fontSize: "11px" },
  formatter: function (this: any) {
    const chart: Highcharts.Chart | undefined =
      this.points?.[0]?.series?.chart ?? this.series?.chart;
    const hoveredX = typeof this.x === "number" ? this.x : this.points?.[0]?.x;

    if (!chart || typeof hoveredX !== "number") return false;

    const rows = chart.series
      .filter(
        (series) => series.visible && series.options?.showInLegend !== false,
      )
      .map((series) => {
        const point = nearestPointAtX(series, hoveredX);
        const seriesTooltip = (
          series.options as {
            tooltip?: Highcharts.SeriesTooltipOptionsObject;
          }
        )?.tooltip;
        const decimals = seriesTooltip?.valueDecimals ?? 2;
        const suffix = seriesTooltip?.valueSuffix ?? "";
        const numericValue =
          point?.y !== null &&
          point?.y !== undefined &&
          Number.isFinite(Number(point.y))
            ? Number(point.y)
            : null;
        const value =
          numericValue === null
            ? "—"
            : `${numericValue.toFixed(decimals)}${suffix}`;
        const seriesName = String(series.name ?? "");

        return {
          numericValue,
          seriesName,
          html: `<div style="display:flex;align-items:center;gap:6px;line-height:1.55;white-space:nowrap;">
            <span style="width:9px;height:9px;border-radius:2px;flex:0 0 auto;background:${series.color};"></span>
            <span style="flex:1 1 auto;">${escapeTooltipText(seriesName)}</span>
            <b style="margin-left:10px;">${value}</b>
          </div>`,
        };
      })
      // Highest reading first. Missing values remain at the bottom; equal values
      // are ordered by series name so the tooltip stays stable while hovering.
      .sort((left, right) => {
        if (left.numericValue === null && right.numericValue === null) {
          return left.seriesName.localeCompare(right.seriesName);
        }
        if (left.numericValue === null) return 1;
        if (right.numericValue === null) return -1;

        return (
          right.numericValue - left.numericValue ||
          left.seriesName.localeCompare(right.seriesName)
        );
      })
      .map((row) => row.html);

    if (rows.length === 0) return false;

    // Many pumps would otherwise make the tooltip taller than the window.
    const columnCount = rows.length > 14 ? 2 : 1;

    return `<div style="min-width:180px;">
        <div style="font-weight:700;margin-bottom:5px;">${Highcharts.dateFormat(
          "%m/%d/%Y %I:%M:%S %p",
          hoveredX,
        )}</div>
        <div style="column-count:${columnCount};column-gap:20px;">${rows.join("")}</div>
      </div>`;
  },
};

// Dedicated pop-out tab. Not a pump channel — treatment rows come from
// /treatment/channels (historical) and /treatment/channels/incremental (live).
// These endpoints are called only in this window, never on the Fleet page.
const PJR_TAB_VALUE = "__pjr__";
const PJR_LIVE_LOOKBACK_MS = 10 * 60 * 1000;
const PJR_CHANNELS_CSV =
  "density,slurry_rate,clean_rate,surface_treating_pressure,bottom_hole_pressure";
const NET_PRESSURE_CHANNEL = "net_pressure";

interface PjrChannelDef {
  value: string;
  label: string;
  unit: string;
  axis: AxisName;
  color: string;
}

const PJR_CHANNEL_DEFS: PjrChannelDef[] = [
  {
    value: "surface_treating_pressure",
    label: "Surface Treating Pressure",
    unit: "psi",
    axis: "left1",
    color: "#ef4444",
  },
  {
    value: "bottom_hole_pressure",
    label: "Bottom Hole Pressure",
    unit: "psi",
    axis: "left1",
    color: "#c084fc",
  },
  {
    value: "slurry_rate",
    label: "Slurry Rate",
    unit: "bpm",
    axis: "right1",
    color: "#8cc540",
  },
  {
    value: "clean_rate",
    label: "Clean Rate",
    unit: "bpm",
    axis: "right1",
    color: "#3e76fe",
  },
  {
    value: "density",
    label: "Density",
    unit: "ppa",
    axis: "right2",
    color: "#f59e0b",
  },
];

const PJR_CHANNEL_VALUES = PJR_CHANNEL_DEFS.map((channel) => channel.value);
const TREATMENT_POPOUT_CHANNEL_VALUES = [...PJR_CHANNEL_VALUES, "net_pressure"];

const CHEM_RATE_TAB_VALUE = "__chemical_rate__";
const CHEM_RATIO_TAB_VALUE = "__chemical_ratio__";
const NET_PRESSURE_TAB_VALUE = "__net_pressure__";
const CHEMICAL_CHANNELS_CSV = "chemical_rate,chemical_ratio";
const CHEMICAL_CHANNEL_VALUES = ["chemical_rate", "chemical_ratio"];
const CHEMICAL_SERIES_COLORS = [
  "#8cc540",
  "#3e76fe",
  "#f59e0b",
  "#ef4444",
  "#c084fc",
  "#22d3ee",
  "#f472b6",
  "#a3e635",
];

const SPECIAL_POPOUT_TABS = [
  { label: "PJR", value: PJR_TAB_VALUE },
  { label: "Chemical (Rate)", value: CHEM_RATE_TAB_VALUE },
  { label: "Chemical (Ratio)", value: CHEM_RATIO_TAB_VALUE },
  { label: "Net Pressure", value: NET_PRESSURE_TAB_VALUE },
] as const;

const SPECIAL_POPOUT_CHANNEL_VALUES = new Set([
  PJR_TAB_VALUE,
  CHEM_RATE_TAB_VALUE,
  CHEM_RATIO_TAB_VALUE,
  NET_PRESSURE_TAB_VALUE,
]);

const AXIS_INDEX: Record<AxisName, number> = {
  left1: 0,
  left2: 1,
  right1: 2,
  right2: 3,
};

const pjrSeriesName = (
  channel: PjrChannelDef,
  wellKey: string,
  wellCount: number,
  wellName?: string,
) => {
  const label = `${channel.label} (${channel.unit})`;
  if (wellCount <= 1 || wellKey === "fleet") return label;
  return `${label} · ${wellName || `Well ${wellKey}`}`;
};

interface TreatmentChannelRow {
  time_stamp: number;
  fleet?: number;
  well_id?: number | null;
  stage_id?: number | null;
  chemical_name?: string | null;
  [key: string]: unknown;
}

const wellKeyForTreatmentRow = (row: TreatmentChannelRow): string => {
  const wellId = Number(row?.well_id);
  return Number.isFinite(wellId) && wellId > 0 ? String(wellId) : "fleet";
};

const chemicalKeyForRow = (row: TreatmentChannelRow): string => {
  const name = String(row?.chemical_name ?? "").trim();
  return name || "chemical";
};

const keyedRowsToBuffers = (
  rows: TreatmentChannelRow[],
  channels: string[],
  keyForRow: (row: TreatmentChannelRow) => string,
): FleetBuffers => {
  const grouped = new Map<string, Map<string, Array<[number, number]>>>();

  rows.forEach((row) => {
    const timestamp = Number(row?.time_stamp);
    if (!Number.isFinite(timestamp)) return;

    const rowKey = keyForRow(row);
    let channelMap = grouped.get(rowKey);
    if (!channelMap) {
      channelMap = new Map();
      grouped.set(rowKey, channelMap);
    }

    channels.forEach((channel) => {
      const raw = row[channel];
      if (raw === null || raw === undefined || raw === "") return;
      const value = Number(raw);
      if (!Number.isFinite(value)) return;
      const points = channelMap!.get(channel) || [];
      points.push([timestamp, value]);
      channelMap!.set(channel, points);
    });
  });

  const result: FleetBuffers = new Map();
  grouped.forEach((channelMap, rowKey) => {
    const buffersByChannel = new Map<string, ChannelBuffer>();
    channelMap.forEach((points, channel) => {
      const byTime = new Map<number, number>();
      points.forEach(([timestamp, value]) => byTime.set(timestamp, value));
      const times = Array.from(byTime.keys()).sort((a, b) => a - b);
      if (times.length === 0) return;
      buffersByChannel.set(channel, {
        t: Float64Array.from(times),
        v: Float32Array.from(times.map((time) => byTime.get(time) as number)),
      });
    });
    if (buffersByChannel.size > 0) result.set(rowKey, buffersByChannel);
  });

  return result;
};

const treatmentRowsToBuffers = (
  rows: TreatmentChannelRow[],
  channels: string[],
): FleetBuffers => keyedRowsToBuffers(rows, channels, wellKeyForTreatmentRow);

const chemicalRowsToBuffers = (rows: TreatmentChannelRow[]): FleetBuffers =>
  keyedRowsToBuffers(rows, CHEMICAL_CHANNEL_VALUES, chemicalKeyForRow);

const buffersHaveChannel = (
  source: FleetBuffers | null,
  channelValue?: string,
): boolean => {
  if (!source) return false;

  return Array.from(source.values()).some((channelMap) => {
    if (channelValue) {
      const buffer = channelMap.get(channelValue);
      return Boolean(buffer && buffer.t.length > 0);
    }
    return Array.from(channelMap.values()).some(
      (buffer) => buffer && buffer.t.length > 0,
    );
  });
};

const externalFeedErrorMessage = (err: unknown, label: string): string => {
  const data = (err as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const code = String(
    data?.error || data?.code || data?.error_code || "",
  ).toUpperCase();

  if (code === "RANGE_TOO_LARGE") {
    return "Time range is too large for this resolution. Narrow the window.";
  }
  if (code === "RANGE_INVALID" || code === "MISSING_RANGE") {
    return `Invalid time range for ${label} data.`;
  }
  if (code === "CHANNEL_INVALID") {
    return `One or more ${label} channels are not valid for this fleet.`;
  }
  if (code === "RESOLUTION_INVALID") {
    return `Invalid resolution for ${label} data.`;
  }
  if (code === "MISSING_FLEET") {
    return `Missing fleet for ${label} data.`;
  }

  return `Failed to load ${label} chart data.`;
};

const pjrErrorMessage = (err: unknown): string =>
  externalFeedErrorMessage(err, "PJR");

const chemicalErrorMessage = (err: unknown): string =>
  externalFeedErrorMessage(err, "chemical");

// Union two sorted sample buffers, deduped by timestamp (`base` wins a tie).
// Both inputs are ascending, so this is a single linear merge pass.
const mergeChannelBuffers = (
  base: ChannelBuffer,
  incoming: ChannelBuffer,
): ChannelBuffer => {
  if (base.t.length === 0) return incoming;
  if (incoming.t.length === 0) return base;

  const capacity = base.t.length + incoming.t.length;
  const t = new Float64Array(capacity);
  const v = new Float32Array(capacity);

  let baseIndex = 0;
  let incomingIndex = 0;
  let out = 0;

  while (baseIndex < base.t.length || incomingIndex < incoming.t.length) {
    const baseTime =
      baseIndex < base.t.length ? base.t[baseIndex] : Number.POSITIVE_INFINITY;
    const incomingTime =
      incomingIndex < incoming.t.length
        ? incoming.t[incomingIndex]
        : Number.POSITIVE_INFINITY;

    if (baseTime <= incomingTime) {
      t[out] = baseTime;
      v[out] = base.v[baseIndex];
      baseIndex += 1;
      if (baseTime === incomingTime) incomingIndex += 1;
    } else {
      t[out] = incomingTime;
      v[out] = incoming.v[incomingIndex];
      incomingIndex += 1;
    }

    out += 1;
  }

  return out === capacity
    ? { t, v }
    : { t: t.subarray(0, out), v: v.subarray(0, out) };
};

// Merge two buffer maps over the union of pumps, channels, AND samples. A union
// (not "one side wins") is required because the two sides can cover different
// spans of the same channel: a pop-out opened mid-stream is seeded from the
// opener's live buffers and the streaming poll starts appending immediately,
// while the all-tab prefetch below carries the historical window — whichever
// lands second must extend the other, not replace it.
const mergeBuffers = (
  base: FleetBuffers | null,
  incoming: FleetBuffers,
): FleetBuffers => {
  if (!base || base.size === 0) return incoming;

  const merged: FleetBuffers = new Map();
  const pumpNames = new Set<string>([
    ...Array.from(base.keys()),
    ...Array.from(incoming.keys()),
  ]);

  pumpNames.forEach((pumpName) => {
    const baseChannels = base.get(pumpName);
    const incomingChannels = incoming.get(pumpName);
    const channelNames = new Set<string>([
      ...Array.from(baseChannels?.keys() || []),
      ...Array.from(incomingChannels?.keys() || []),
    ]);
    const channelMap = new Map<string, ChannelBuffer>();

    channelNames.forEach((channel) => {
      const baseBuffer = baseChannels?.get(channel);
      const incomingBuffer = incomingChannels?.get(channel);
      const mergedBuffer =
        baseBuffer && incomingBuffer
          ? mergeChannelBuffers(baseBuffer, incomingBuffer)
          : baseBuffer || incomingBuffer;

      if (mergedBuffer && mergedBuffer.t.length > 0) {
        channelMap.set(channel, mergedBuffer);
      }
    });

    if (channelMap.size > 0) merged.set(pumpName, channelMap);
  });

  return merged;
};

const rowsToBuffers = (
  rows: PumpData[],
  channels: string[],
  selectedPumps: string[],
): FleetBuffers => {
  const selectedPumpSet =
    selectedPumps.length > 0 ? new Set(selectedPumps) : null;
  const grouped = new Map<string, Map<string, Array<[number, number]>>>();

  rows.forEach((row) => {
    const pumpName = String(row?.pump_name || "").trim();
    const timestamp = Number(row?.time_stamp);
    if (
      !pumpName ||
      !Number.isFinite(timestamp) ||
      (selectedPumpSet && !selectedPumpSet.has(pumpName))
    ) {
      return;
    }

    let channelMap = grouped.get(pumpName);
    if (!channelMap) {
      channelMap = new Map();
      grouped.set(pumpName, channelMap);
    }

    channels.forEach((channel) => {
      const value = toWholeNumberReading(channel, Number(row?.[channel]));
      if (!Number.isFinite(value)) return;
      const points = channelMap!.get(channel) || [];
      points.push([timestamp, value]);
      channelMap!.set(channel, points);
    });
  });

  const result: FleetBuffers = new Map();
  grouped.forEach((channelMap, pumpName) => {
    const buffersByChannel = new Map<string, any>();
    channelMap.forEach((points, channel) => {
      points.sort((a, b) => a[0] - b[0]);
      buffersByChannel.set(channel, {
        t: Float64Array.from(points.map(([timestamp]) => timestamp)),
        v: Float64Array.from(points.map(([, value]) => value)),
      });
    });
    result.set(pumpName, buffersByChannel);
  });

  return result;
};

type AlarmSeverity = "normal" | "warning" | "critical";

interface AlarmEvent {
  pump?: string;
  source?: string;
  pump_name?: string;
  alarm?: string;
  name?: string;
  severity?: string;
  alarm_time?: string;
  time_stamp?: string | number;
  timestamp?: string | number;
  [key: string]: any;
}

const backendUrl =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

const getAuthToken = () =>
  localStorage.getItem("jwt") || localStorage.getItem("token");

const appendWellIds = (query: URLSearchParams, wellIds: number[]) => {
  wellIds.forEach((wellId) => query.append("well_id", String(wellId)));
};

const fetchTreatmentChannelRows = async (args: {
  fleet: number;
  startTime: number;
  endTime: number;
  channels: string;
  wellIds: number[];
  token: string;
}): Promise<{ rows: TreatmentChannelRow[]; truncated: boolean }> => {
  const query = new URLSearchParams({
    fleet: String(args.fleet),
    start_time: String(args.startTime),
    end_time: String(args.endTime),
    channels: args.channels,
    resolution: "auto",
  });
  appendWellIds(query, args.wellIds);

  const response = await axios.get(
    `${backendUrl}/treatment/channels?${query.toString()}`,
    {
      headers: { Authorization: `Bearer ${args.token}` },
      timeout: 180000,
    },
  );

  return {
    rows: Array.isArray(response.data?.data) ? response.data.data : [],
    truncated: Boolean(response.data?.truncated),
  };
};

const loadTreatmentPopoutBuffers = async (args: {
  fleet: number;
  startTime: number;
  endTime: number;
  wellIds: number[];
  token: string;
}): Promise<{ buffers: FleetBuffers; truncated: boolean }> => {
  const [pjrResult, netResult] = await Promise.all([
    fetchTreatmentChannelRows({ ...args, channels: PJR_CHANNELS_CSV }),
    fetchTreatmentChannelRows({
      ...args,
      channels: NET_PRESSURE_CHANNEL,
    }).catch((error) => {
      console.error("Failed to load net pressure channels:", error);
      return { rows: [] as TreatmentChannelRow[], truncated: false };
    }),
  ]);

  return {
    buffers: mergeBuffers(
      treatmentRowsToBuffers(pjrResult.rows, PJR_CHANNEL_VALUES),
      treatmentRowsToBuffers(netResult.rows, [NET_PRESSURE_CHANNEL]),
    ),
    truncated: pjrResult.truncated || netResult.truncated,
  };
};
const STREAMING_WINDOW_MS = 3 * 60 * 60 * 1000;
const STREAMING_POLL_INTERVAL_MS = 1_000;
type ChartPoint = [number, number | null];

const formatLocalDateTimeForApi = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
};

const normalizeAlarmText = (value: any) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normalizeAlarmSeverity = (alarm: AlarmEvent): AlarmSeverity => {
  const severity = normalizeAlarmText(alarm?.severity);

  if (severity.includes("critical")) return "critical";
  if (severity.includes("warning")) return "warning";

  const alarmText = normalizeAlarmText(alarm?.alarm || alarm?.name);

  if (
    alarmText.includes("critical") ||
    alarmText.includes("allstop") ||
    alarmText.includes("all stop") ||
    alarmText.includes("stop button") ||
    alarmText.includes("shutdown") ||
    alarmText.includes("trip") ||
    alarmText.includes("over max")
  ) {
    return "critical";
  }

  if (
    alarmText.includes("warning") ||
    alarmText.includes("warn") ||
    alarmText.includes("low") ||
    alarmText.includes("high") ||
    alarmText.includes("alarm") ||
    alarmText.includes("failure") ||
    alarmText.includes("loss") ||
    alarmText.includes("disconnected") ||
    alarmText.includes("detected") ||
    alarmText.includes("voltage") ||
    alarmText.includes("pressure") ||
    alarmText.includes("lamp") ||
    alarmText.includes("communication")
  ) {
    return "warning";
  }

  return "normal";
};

const getAlarmSeverityMeta = (severity: AlarmSeverity) => {
  switch (severity) {
    case "critical":
      return {
        label: "Critical",
        color: "#ef4444",
        marker: "⬣",
      };
    case "warning":
      return {
        label: "Warning",
        color: "#f59e0b",
        marker: "⚠",
      };
    default:
      return {
        label: "Normal",
        color: "#22c55e",
        marker: "●",
      };
  }
};

const getHighestAlarmSeverity = (alarms: AlarmEvent[]): AlarmSeverity => {
  if (alarms.some((alarm) => normalizeAlarmSeverity(alarm) === "critical")) {
    return "critical";
  }

  if (alarms.some((alarm) => normalizeAlarmSeverity(alarm) === "warning")) {
    return "warning";
  }

  return "normal";
};

const ChartPopout: React.FC = () => {
  // Full-resolution typed buffers (per pump -> per channel), either handed off
  // from the opener window (instant, no DB) or fetched columnar as a fallback.
  const [buffers, setBuffers] = useState<FleetBuffers | null>(null);
  const [alarmEvents, setAlarmEvents] = useState<AlarmEvent[]>([]);
  const [selectedAlarmEvents, setSelectedAlarmEvents] = useState<AlarmEvent[]>(
    [],
  );
  const [alarmSeverityFilter, setAlarmSeverityFilter] = useState<
    "critical" | "warning"
  >("critical");
  const [showSeverityMenu, setShowSeverityMenu] = useState(false);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The one-shot all-tab prefetch: until it resolves, tabs whose channel has no
  // samples yet stay disabled so a click can't land on a not-yet-loaded channel.
  const [prefetchComplete, setPrefetchComplete] = useState(false);
  const [prefetchError, setPrefetchError] = useState("");
  const [reloadRequest, setReloadRequest] = useState(0);
  const [selectedChannelValues, setSelectedChannelValues] = useState<string[]>(
    [],
  );
  const [showChannelEditor, setShowChannelEditor] = useState(false);
  const [channelSearch, setChannelSearch] = useState("");
  const [draftChannelValues, setDraftChannelValues] = useState<string[]>([]);
  // Special views such as PJR/Chemical/Net Pressure are navigation tabs only.
  // Keep them separate from the editable pump-channel selection so opening a
  // special tab never wipes out the channels the user added to the graph.
  const [activeSpecialTabValue, setActiveSpecialTabValue] = useState<
    string | null
  >(null);
  const [channelOptions, setChannelOptions] =
    useState<PumpChannel[]>(ALL_PUMP_CHANNELS);
  const [popoutAxisSettings, setPopoutAxisSettings] =
    useState<PopoutAxisSettings>(DEFAULT_AXIS_SETTINGS);
  const [popoutAxisAutoScale, setPopoutAxisAutoScale] = useState<
    Record<AxisName, boolean>
  >({ left1: true, left2: true, right1: true, right2: true });
  const [popoutAxisExtremes, setPopoutAxisExtremes] =
    useState<PopoutAxisExtremes>({
      left1: {},
      left2: {},
      right1: {},
      right2: {},
    });
  const streamingPollRef = useRef<number | null>(null);
  const streamingPollInFlightRef = useRef(false);
  const latestStreamingTimestampRef = useRef(0);
  const [pjrBuffers, setPjrBuffers] = useState<FleetBuffers | null>(null);
  const [pjrLoading, setPjrLoading] = useState(false);
  const [pjrError, setPjrError] = useState("");
  const [pjrPrefetchComplete, setPjrPrefetchComplete] = useState(false);
  const [pjrReloadRequest, setPjrReloadRequest] = useState(0);
  const pjrPollRef = useRef<number | null>(null);
  const pjrPollInFlightRef = useRef(false);
  const pjrCursorRef = useRef<number | null>(null);
  const [chemicalBuffers, setChemicalBuffers] = useState<FleetBuffers | null>(
    null,
  );
  const [chemicalLoading, setChemicalLoading] = useState(false);
  const [chemicalError, setChemicalError] = useState("");
  const [chemicalPrefetchComplete, setChemicalPrefetchComplete] =
    useState(false);
  const [chemicalReloadRequest, setChemicalReloadRequest] = useState(0);
  const chemicalPollRef = useRef<number | null>(null);
  const chemicalPollInFlightRef = useRef(false);
  const chemicalCursorRef = useRef<number | null>(null);

  const [axisValueEdit, setAxisValueEdit] = useState<AxisValueEditState | null>(
    null,
  );
  const [axisValueInput, setAxisValueInput] = useState("");

  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<Highcharts.Chart | null>(null);
  const channelTabsRef = useRef<HTMLDivElement | null>(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  const openAxisValueEditor = useCallback(
    (payload: Omit<AxisValueEditState, "error">) => {
      setAxisValueEdit(payload);
      setAxisValueInput(String(Number(payload.clickedValue.toFixed(2))));
    },
    [],
  );

  const closeAxisValueEditor = useCallback(() => {
    setAxisValueEdit(null);
    setAxisValueInput("");
  }, []);

  const resetAxisValueToAuto = useCallback(() => {
    if (!axisValueEdit) return;

    axisValueEdit.axis.setExtremes(undefined, undefined, true, false);
    closeAxisValueEditor();
  }, [axisValueEdit, closeAxisValueEditor]);

  const applyAxisValueEdit = useCallback(() => {
    if (!axisValueEdit) return;

    const trimmed = axisValueInput.trim();

    if (!trimmed) {
      setAxisValueEdit((prev) =>
        prev ? { ...prev, error: "Enter a value or use Reset Auto." } : prev,
      );
      return;
    }

    const numericValue = Number(trimmed);

    if (!Number.isFinite(numericValue)) {
      setAxisValueEdit((prev) =>
        prev ? { ...prev, error: "Please enter a valid number." } : prev,
      );
      return;
    }

    const currentRange = axisValueEdit.currentMax - axisValueEdit.currentMin;

    if (!Number.isFinite(currentRange) || currentRange <= 0) {
      setAxisValueEdit((prev) =>
        prev
          ? { ...prev, error: "Unable to calculate the current axis range." }
          : prev,
      );
      return;
    }

    const nextMin = numericValue - axisValueEdit.ratioFromBottom * currentRange;
    const nextMax =
      numericValue + (1 - axisValueEdit.ratioFromBottom) * currentRange;

    if (nextMin >= nextMax) {
      setAxisValueEdit((prev) =>
        prev
          ? { ...prev, error: "Axis minimum must be less than axis maximum." }
          : prev,
      );
      return;
    }

    axisValueEdit.axis.setExtremes(nextMin, nextMax, true, false);
    closeAxisValueEditor();
  }, [axisValueEdit, axisValueInput, closeAxisValueEditor]);

  const params = useMemo(() => {
    const search = new URLSearchParams(window.location.search);

    const mode = search.get("mode") === "asset" ? "asset" : "fleet";
    const fleetParam = search.get("fleet");
    const fleet = fleetParam ? Number(fleetParam) : null;
    const startTime = Number(search.get("start_time"));
    const endTime = Number(search.get("end_time"));

    const channels = (search.get("channels") || "discharge_pressure")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    const pumps = (search.get("pumps") || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    const assetPumpNames = search
      .getAll("pump_name")
      .map((value) => value.trim())
      .filter(Boolean);

    const handoffKey = search.get("handoff") || "";
    const fleetType =
      search.get("fleet_type") === "electric" ? "electric" : "diesel";
    const realtime = search.get("realtime") === "1";
    const wellIds = search
      .getAll("well_id")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
    const wellNames = search
      .getAll("well_name")
      .map((value) => value.trim())
      .filter(Boolean);
    const wellNamesById: Record<string, string> = {};
    wellIds.forEach((wellId, index) => {
      if (wellNames[index]) wellNamesById[String(wellId)] = wellNames[index];
    });
    const padName = (search.get("pad_name") || "").trim();

    return {
      mode,
      fleet,
      startTime,
      endTime,
      channels,
      pumps,
      assetPumpNames,
      handoffKey,
      fleetType,
      realtime,
      wellIds,
      wellNames,
      wellNamesById,
      padName,
    };
  }, []);

  useEffect(() => {
    const initialChannels =
      params.channels.length > 0 ? params.channels : ["rpm"];
    setActiveSpecialTabValue(null);
    setSelectedChannelValues(initialChannels);
    setDraftChannelValues(initialChannels);
  }, [params.channels]);

  const selectedChannels = useMemo(() => {
    const selectedValues =
      selectedChannelValues.length > 0
        ? selectedChannelValues
        : params.channels.length > 0
          ? params.channels
          : ["rpm"];

    const resolved: PumpChannel[] = [];
    let insertedPowerEndGroup = false;

    selectedValues.forEach((value) => {
      if (SPECIAL_POPOUT_CHANNEL_VALUES.has(value)) return;

      if (isPowerEndTemperatureValue(value)) {
        if (insertedPowerEndGroup) return;
        insertedPowerEndGroup = true;

        POWER_END_TEMPERATURE_CHANNELS.forEach((fallback) => {
          const found =
            channelOptions.find(
              (channel) => channel.value === fallback.value,
            ) || findChannelByValue(fallback.value);

          resolved.push(found || fallback);
        });
        return;
      }

      const found =
        channelOptions.find((channel) => channel.value === value) ||
        findChannelByValue(value);

      resolved.push(
        found || {
          value,
          label: value
            .replace(/_/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
        },
      );
    });

    return resolved.filter(
      (channel, index, channels) =>
        channels.findIndex((candidate) => candidate.value === channel.value) ===
        index,
    );
  }, [channelOptions, params.channels, selectedChannelValues]);

  const tabTemplate = useMemo(
    () =>
      params.fleetType === "electric"
        ? ELECTRIC_TAB_TEMPLATE
        : DIESEL_TAB_TEMPLATE,
    [params.fleetType],
  );

  // Every candidate of every tab, plus the channel(s) the pop-out was opened
  // with. This whole list is fetched in ONE columnar request so switching tabs
  // afterwards is a pure client-side re-render — no refetch, no DB hit.
  const prefetchChannels = useMemo(() => {
    const editableSelections = selectedChannelValues.filter(
      (value) => !SPECIAL_POPOUT_CHANNEL_VALUES.has(value),
    );

    const values = [
      ...params.channels,
      ...editableSelections,
      ...tabTemplate.flatMap((tab) => tab.candidates),
    ];

    return Array.from(new Set(values.filter(Boolean)));
  }, [params.channels, selectedChannelValues, tabTemplate]);

  const channelHasData = useCallback(
    (channelValue: string) => {
      if (!buffers) return false;

      for (const channelMap of Array.from(buffers.values())) {
        const buffer = channelMap.get(channelValue);
        if (buffer && buffer.t.length > 0) return true;
      }

      return false;
    },
    [buffers],
  );

  const labelForChannel = useCallback(
    (channelValue: string) => {
      const found =
        channelOptions.find((channel) => channel.value === channelValue) ||
        findChannelByValue(channelValue);

      return (
        found?.label ||
        channelValue
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
      );
    },
    [channelOptions],
  );

  const editableChannelOptions = useMemo(() => {
    const byValue = new Map<string, PumpChannel>();

    [
      ...ALL_PUMP_CHANNELS,
      ...channelOptions,
      ...POWER_END_TEMPERATURE_CHANNELS,
    ].forEach((channel) => {
      const value = String(channel?.value || "").trim();
      if (!value || SPECIAL_POPOUT_CHANNEL_VALUES.has(value)) return;
      if (value === "pe_temp" || value === "pe_outlet_temp") return;

      if (!byValue.has(value)) {
        byValue.set(value, channel);
      }
    });

    return Array.from(byValue.values()).sort((left, right) =>
      String(left.label || left.value).localeCompare(
        String(right.label || right.value),
        undefined,
        { numeric: true, sensitivity: "base" },
      ),
    );
  }, [channelOptions]);

  const filteredEditableChannelOptions = useMemo(() => {
    const query = channelSearch.trim().toLowerCase();
    if (!query) return editableChannelOptions;

    return editableChannelOptions.filter((channel) =>
      `${channel.label} ${channel.value}`.toLowerCase().includes(query),
    );
  }, [channelSearch, editableChannelOptions]);

  const channelTabs = useMemo(() => {
    // Power End Temperature is one grouped business view even when the opener
    // handed over generic + inlet + outlet as three selected channels.
    const editableSelections = selectedChannelValues.filter(
      (value) => !SPECIAL_POPOUT_CHANNEL_VALUES.has(value),
    );
    const tabSourceValues =
      editableSelections.length > 0 ? editableSelections : params.channels;

    const openedPowerEndValue = tabSourceValues.find((value) =>
      isPowerEndTemperatureValue(value),
    );

    // Currently displayed channels lead the strip. Collapse the Power End
    // temperature family to one tab instead of three near-duplicates.
    const openedTabs = openedPowerEndValue
      ? [
          {
            label: PE_OIL_TEMP_TAB.label,
            value: openedPowerEndValue,
          },
          ...tabSourceValues
            .filter((value) => !isPowerEndTemperatureValue(value))
            .map((value) => ({
              label: labelForChannel(value),
              value,
            })),
        ]
      : tabSourceValues.map((value) => ({
          label: labelForChannel(value),
          value,
        }));

    const templateTabs = tabTemplate.map((tab) => ({
      label: tab.label,
      value:
        tab === PE_OIL_TEMP_TAB && openedPowerEndValue
          ? openedPowerEndValue
          : tab.candidates.find((candidate) => channelHasData(candidate)) ||
            tab.candidates[0],
    }));

    const specialTabs =
      params.mode === "fleet" && params.fleet
        ? SPECIAL_POPOUT_TABS.map((tab) => ({
            label: tab.label,
            value: tab.value,
          }))
        : [];

    return [...openedTabs, ...specialTabs, ...templateTabs].filter(
      (tab, index, tabs) =>
        tabs.findIndex((other) => other.value === tab.value) === index,
    );
  }, [
    channelHasData,
    labelForChannel,
    params.channels,
    params.fleet,
    params.mode,
    selectedChannelValues,
    tabTemplate,
  ]);

  const updateChannelTabScrollState = useCallback(() => {
    const element = channelTabsRef.current;
    if (!element) {
      setCanScrollTabsLeft(false);
      setCanScrollTabsRight(false);
      return;
    }

    const maxScrollLeft = Math.max(
      0,
      element.scrollWidth - element.clientWidth,
    );
    setCanScrollTabsLeft(element.scrollLeft > 1);
    setCanScrollTabsRight(maxScrollLeft - element.scrollLeft > 1);
  }, []);

  const scrollChannelTabs = useCallback((direction: -1 | 1) => {
    const element = channelTabsRef.current;
    if (!element) return;

    const distance = Math.max(240, Math.floor(element.clientWidth * 0.7));
    element.scrollBy({ left: direction * distance, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const element = channelTabsRef.current;
    if (!element) return;

    const update = () => updateChannelTabScrollState();
    const frame = window.requestAnimationFrame(update);
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [channelTabs, updateChannelTabScrollState]);

  const getAxisNameForChannel = useCallback(
    (channelValue: string): AxisName => {
      const axisNames: AxisName[] = ["left1", "left2", "right1", "right2"];
      const hasPowerEndGroup = selectedChannels.some((channel) =>
        isPowerEndTemperatureValue(channel.value),
      );

      // Power End Inlet + Outlet share one axis: the one the main chart's axis
      // settings gave them (their own axis when one was free), else left1.
      const powerEndAxis: AxisName =
        axisNames.find((axisName) =>
          (popoutAxisSettings[axisName]?.channels || []).some((value) =>
            isPowerEndTemperatureValue(value),
          ),
        ) ?? "left1";

      if (hasPowerEndGroup && isPowerEndTemperatureValue(channelValue)) {
        return powerEndAxis;
      }

      if (selectedChannels.length <= 1) return "left1";

      const configuredAxis = axisNames.find((axisName) =>
        popoutAxisSettings[axisName]?.channels?.includes(channelValue),
      );

      if (
        configuredAxis &&
        !(hasPowerEndGroup && configuredAxis === powerEndAxis)
      ) {
        return configuredAxis;
      }

      const logicalValues: string[] = [];
      let powerEndInserted = false;
      selectedChannels.forEach((channel) => {
        if (isPowerEndTemperatureValue(channel.value)) {
          if (!powerEndInserted) {
            logicalValues.push("__power_end_temperature__");
            powerEndInserted = true;
          }
          return;
        }
        logicalValues.push(channel.value);
      });

      const logicalValue = isPowerEndTemperatureValue(channelValue)
        ? "__power_end_temperature__"
        : channelValue;
      const logicalIndex = Math.max(0, logicalValues.indexOf(logicalValue));
      return axisNames[Math.min(logicalIndex, axisNames.length - 1)];
    },
    [popoutAxisSettings, selectedChannels],
  );

  useEffect(() => {
    const missingFleet = params.mode === "fleet" && !params.fleet;
    const missingAssetPumps =
      params.mode === "asset" && params.assetPumpNames.length === 0;

    if (
      missingFleet ||
      missingAssetPumps ||
      !params.startTime ||
      !params.endTime
    ) {
      setError("Missing chart parameters.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setPrefetchError("");
    setPrefetchComplete(false);

    let cancelled = false;
    // Buffers handed over by the opener, used for the FIRST paint only. The
    // all-tab prefetch below still runs, and merges on top of this seed.
    let seededBuffers: FleetBuffers | null = null;

    // 1) Instant path: reuse the opener window's already-parsed typed buffers
    // (same-origin, by reference) — no refetch, no DB hit.
    try {
      const handoff = (window.opener as any)?.__pumpChartHandoff as
        | PumpChartHandoff
        | undefined;
      // The handoff only seeds the FIRST paint. The prefetch below always runs
      // (it has to, for the other tabs) and sends the URL's well filter, so a
      // well-filtered pop-out still converges on correctly filtered data even
      // though the handed buffers do not retain well_id.
      if (handoff && handoff.key === params.handoffKey) {
        if (handoff.axisSettings) {
          setPopoutAxisSettings(handoff.axisSettings);
        }
        if (handoff.axisAutoScale) {
          setPopoutAxisAutoScale(handoff.axisAutoScale);
        }
        if (handoff.axisExtremes) {
          setPopoutAxisExtremes(handoff.axisExtremes);
        }
        if (
          Array.isArray(handoff.channelOptions) &&
          handoff.channelOptions.length
        ) {
          setChannelOptions(handoff.channelOptions);
        }

        const openedChannels =
          params.channels.length > 0 ? params.channels : ["rpm"];

        const handedFullResolutionBuffers = () => {
          if (
            !handoff.buffers ||
            typeof handoff.buffers.size !== "number" ||
            handoff.buffers.size === 0
          ) {
            return null;
          }

          const handedBuffers = handoff.buffers as FleetBuffers;
          const hasEveryOpenedChannel = openedChannels.every((channel) =>
            Array.from(handedBuffers.values()).some((channelMap) =>
              channelMap.has(channel),
            ),
          );

          return hasEveryOpenedChannel ? handedBuffers : null;
        };

        const handedRealtimeRows = () => {
          if (!Array.isArray(handoff.rows) || handoff.rows.length === 0) {
            return null;
          }

          const liveBuffers = rowsToBuffers(
            handoff.rows as PumpData[],
            openedChannels,
            params.mode === "asset" ? params.assetPumpNames : params.pumps,
          );
          const hasEveryOpenedChannel = openedChannels.every((channel) =>
            Array.from(liveBuffers.values()).some((channelMap) =>
              channelMap.has(channel),
            ),
          );

          if (liveBuffers.size === 0 || !hasEveryOpenedChannel) return null;

          return liveBuffers;
        };

        // Historical pop-outs must use the same full-resolution typed buffers as
        // the embedded chart. The row collection is intended for real-time mode
        // and can be sparse, which previously produced broken/cut line segments.
        // Always seed the pop-out with the exact same full-resolution buffers
        // used by Fleet.tsx. Real-time updates are appended afterward by the
        // streaming effect; sparse handoff rows must never replace history.
        seededBuffers = handedFullResolutionBuffers() || handedRealtimeRows();

        if (seededBuffers) {
          // Paint the opened channel immediately; the remaining tabs fill in
          // when the prefetch below resolves.
          setBuffers(seededBuffers);
          setLoading(false);
        }
      }
    } catch {
      // Cross-window access can throw if the opener is gone — fall through.
    }

    // 2) Prefetch EVERY tab channel in one compact columnar request, so tab
    // switching afterwards never hits the network. Extra channels widen the
    // SELECT but scan the same rows, so this costs far less than one request
    // per tab.
    const loadChartData = async () => {
      try {
        const token = getAuthToken();

        const query = new URLSearchParams();
        if (params.mode === "asset") {
          params.assetPumpNames.forEach((pumpName) => {
            query.append("pump_name", pumpName);
          });
        } else if (params.fleet) {
          query.append("fleet", String(params.fleet));
        }
        query.append("start_time", String(params.startTime));
        query.append("end_time", String(params.endTime));
        query.append("resolution", "auto");
        query.append("format", "columnar");
        params.wellIds.forEach((wellId) => {
          query.append("well_id", String(wellId));
        });
        query.append("channels", prefetchChannels.join(","));

        const response = await axios.get(
          `${backendUrl}/pumps?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000,
          },
        );

        if (cancelled) return;

        const parsed = parseColumnarToBuffers(response.data, prefetchChannels);
        let finalBuffers: FleetBuffers = parsed;
        if (params.mode === "fleet" && params.pumps.length > 0) {
          const filteredBuffers: FleetBuffers = new Map();
          params.pumps.forEach((pumpName) => {
            const pumpBuffers = parsed.get(pumpName);
            if (pumpBuffers) filteredBuffers.set(pumpName, pumpBuffers);
          });

          // A stale or differently formatted pump list must not blank the whole
          // pop-out. Use the returned fleet data when no requested pump name
          // matched exactly, while still preferring the requested subset.
          finalBuffers = filteredBuffers.size > 0 ? filteredBuffers : parsed;
        }

        // The seed (and anything the streaming poll has appended to it) wins for
        // the channels it already covers — it reaches the live edge, this
        // historical window does not.
        setBuffers((previous) => mergeBuffers(previous, finalBuffers));
      } catch (err) {
        console.error("Failed to load popout chart:", err);
        if (cancelled) return;

        if (seededBuffers) {
          setPrefetchError(
            "Some channel tabs could not be loaded. Retry to try again.",
          );
        } else {
          setError("Failed to load chart data.");
        }
      } finally {
        if (!cancelled) {
          setPrefetchComplete(true);
          setLoading(false);
        }
      }
    };

    loadChartData();

    return () => {
      cancelled = true;
    };
    // Deliberately NOT dependent on the active tab: the channel list is fetched
    // once per pop-out, so switching tabs never refetches.
  }, [params, prefetchChannels, reloadRequest]);

  useEffect(() => {
    if (!params.realtime || !params.fleet) return;

    const token = getAuthToken();
    if (!token) return;

    const selectedPumpSet =
      params.pumps.length > 0 ? new Set(params.pumps) : null;

    const readChannelValue = (pump: any, channel: string): unknown => {
      const aliases: Record<string, string[]> = {
        engine_temp: ["engine_temp", "engine_coolant_temperature"],
        engine_coolant_temperature: [
          "engine_coolant_temperature",
          "engine_temp",
        ],
        calculated_discharge_rate: [
          "calculated_discharge_rate",
          "discharge_rate",
          "calculated_rate",
        ],
        discharge_vfd_amps: [
          "discharge_vfd_amps",
          "motor_current",
          "vfd_amps",
          "vfd_current",
        ],
        // Tab channels the historical columnar query resolves via a backend
        // alias. Incremental rows carry the raw dbo.pumps column, so map back.
        powerend_oil_pressure: ["powerend_oil_pressure", "pe_pressure"],
        powerend_oil_temperature: ["powerend_oil_temperature", "pe_temp"],
        powerend_oil_inlet_temperature: [
          "powerend_oil_inlet_temperature",
          "pe_temp",
        ],
        powerend_oil_outlet_temperature: [
          "powerend_oil_outlet_temperature",
          "pe_outlet_temp",
        ],
        trans_oil_pressure: ["trans_oil_pressure", "trans_pressure"],
        trans_oil_temperature: ["trans_oil_temperature", "trans_temp"],
        engine_oil_temperature: ["engine_oil_temperature", "engine_oil_temp"],
        engine_oil_temp: ["engine_oil_temp", "engine_oil_temperature"],
        df_displacement: ["df_displacement", "inst_fuel_disp"],
      };

      const candidates = aliases[channel] || [channel];
      for (const key of candidates) {
        const value = pump?.[key];
        if (value !== null && value !== undefined) return value;
      }
      return undefined;
    };

    const normalizeTimestamp = (value: unknown, fallback: number): number => {
      let timestamp = Number(value ?? fallback);
      if (typeof value === "string") timestamp = new Date(value).getTime();
      if (!Number.isFinite(timestamp)) timestamp = fallback;
      if (timestamp < 1_000_000_000_000) timestamp *= 1000;
      return timestamp;
    };

    // Lossless incremental apply: append EVERY row since the cursor (one per pump
    // per second), keep a rolling window anchored at the URL start (capped 3h),
    // and dedup by timestamp. Matches the main chart's transport and semantics —
    // a pump that stops reporting stops advancing (true data, no carry-forward).
    const applyIncrementalRows = (fleetData: any) => {
      const rows: any[] = Array.isArray(fleetData?.pumps)
        ? fleetData.pumps
        : [];
      const maxTsRaw = fleetData?.max_time_stamp;
      if (rows.length === 0 || maxTsRaw === null || maxTsRaw === undefined) {
        return;
      }

      const maxTs = normalizeTimestamp(maxTsRaw, Date.now());
      if (maxTs < latestStreamingTimestampRef.current) return; // out-of-order guard
      latestStreamingTimestampRef.current = maxTs;

      const windowStart = Math.max(
        params.startTime || 0,
        maxTs - STREAMING_WINDOW_MS,
      );

      // Group incoming rows by pump → channel → [t, v].
      const incomingByPump = new Map<
        string,
        Map<string, Array<[number, number]>>
      >();
      rows.forEach((pump) => {
        const pumpName = String(pump?.pump_name || "").trim();
        if (!pumpName || (selectedPumpSet && !selectedPumpSet.has(pumpName))) {
          return;
        }
        const rowTime = normalizeTimestamp(
          pump.time_stamp ?? pump.timestamp,
          maxTs,
        );
        if (rowTime < windowStart || rowTime > maxTs + 5_000) return;

        let channelMap = incomingByPump.get(pumpName);
        if (!channelMap) {
          channelMap = new Map();
          incomingByPump.set(pumpName, channelMap);
        }
        prefetchChannels.forEach((channel) => {
          const rawValue = readChannelValue(pump, channel);
          if (rawValue === null || rawValue === undefined) return;
          const numericValue = Number(rawValue);
          if (!Number.isFinite(numericValue)) return;
          let samples = channelMap!.get(channel);
          if (!samples) {
            samples = [];
            channelMap!.set(channel, samples);
          }
          samples.push([rowTime, numericValue]);
        });
      });

      if (incomingByPump.size === 0) return;

      setBuffers((previous) => {
        const prev: FleetBuffers = previous || new Map();
        const next: FleetBuffers = new Map();
        // Rebuild every pump (existing + incoming) trimmed to the rolling window,
        // so stalled pumps roll off the left edge instead of lingering forever.
        const allPumps = new Set<string>([
          ...Array.from(prev.keys()),
          ...Array.from(incomingByPump.keys()),
        ]);

        allPumps.forEach((pumpName) => {
          if (selectedPumpSet && !selectedPumpSet.has(pumpName)) return;
          const oldChannelMap = prev.get(pumpName) || new Map();
          const incomingChannelMap =
            incomingByPump.get(pumpName) ||
            new Map<string, Array<[number, number]>>();
          const channels = new Set<string>([
            ...Array.from(oldChannelMap.keys()),
            ...Array.from(incomingChannelMap.keys()),
          ]);

          const nextChannelMap = new Map<string, any>();
          channels.forEach((channel) => {
            const merged = new Map<number, number>();
            const existing: any = oldChannelMap.get(channel);
            const oldTimes = existing?.t
              ? Array.from(existing.t as ArrayLike<number>)
              : [];
            const oldValues = Array.from(
              (existing?.v ?? existing?.values ?? []) as ArrayLike<number>,
            );
            for (let i = 0; i < oldTimes.length; i += 1) {
              const time = Number(oldTimes[i]);
              if (time >= windowStart && time <= maxTs) {
                merged.set(time, Number(oldValues[i]));
              }
            }
            (incomingChannelMap.get(channel) || []).forEach(([time, value]) => {
              if (time >= windowStart && time <= maxTs) merged.set(time, value);
            });
            if (merged.size === 0) return;
            const times = Array.from(merged.keys()).sort((a, b) => a - b);
            nextChannelMap.set(channel, {
              t: Float64Array.from(times),
              v: Float64Array.from(
                times.map((time) => merged.get(time) as number),
              ),
            });
          });

          if (nextChannelMap.size > 0) next.set(pumpName, nextChannelMap);
        });

        return next;
      });
    };

    // Cursor for the incremental endpoint = newest applied timestamp.
    //
    // Do NOT seed it from params.endTime. endTime is wall-clock-ish, but fleets
    // do not all stream at wall-clock: some run minutes BEHIND real time (fleet
    // 38 ~14min), others ~1h AHEAD (van clock skew). Seeding endTime makes the
    // first poll ask for rows `since` a timestamp newer than any real sample, so
    // the backend returns nothing and the out-of-order guard then rejects the
    // true (older) edge — the popout never advances. Leaving the cursor at 0
    // makes the first poll omit `since`; the backend returns the recent tail and
    // applyIncrementalRows adopts the real max_time_stamp. Matches the main
    // chart's transport and works regardless of a fleet's clock offset.

    const fetchIncremental = async () => {
      if (streamingPollInFlightRef.current) return;
      streamingPollInFlightRef.current = true;
      try {
        const query = new URLSearchParams({ fleets: String(params.fleet) });
        const since = latestStreamingTimestampRef.current;
        if (since) query.append("since", String(since));
        params.wellIds.forEach((wellId) =>
          query.append("well_id", String(wellId)),
        );
        const response = await axios.get(
          `${backendUrl}/treatment-pump-data-incremental?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10_000,
          },
        );
        const fleetData =
          response.data?.[params.fleet] ??
          response.data?.[String(params.fleet)];
        applyIncrementalRows(fleetData);
      } catch (err) {
        console.error("Popout real-time incremental poll failed:", err);
      } finally {
        streamingPollInFlightRef.current = false;
      }
    };

    void fetchIncremental();
    streamingPollRef.current = window.setInterval(
      fetchIncremental,
      STREAMING_POLL_INTERVAL_MS,
    );

    return () => {
      if (streamingPollRef.current !== null) {
        window.clearInterval(streamingPollRef.current);
        streamingPollRef.current = null;
      }
      streamingPollInFlightRef.current = false;
    };
    // Keyed on the full tab channel list, not the active tab, so switching tabs
    // does not tear down and restart the live poll.
  }, [params, prefetchChannels]);

  // PJR historical seed. Fired when this pop-out opens — not on the Fleet page.
  useEffect(() => {
    if (
      params.mode !== "fleet" ||
      !params.fleet ||
      !params.startTime ||
      !params.endTime
    ) {
      setPjrPrefetchComplete(true);
      setPjrLoading(false);
      return;
    }

    let cancelled = false;
    const token = getAuthToken();

    const loadPjrHistorical = async () => {
      setPjrLoading(true);
      setPjrError("");

      try {
        if (!token) {
          setPjrError("Session expired. Please login again.");
          return;
        }

        const { buffers, truncated } = await loadTreatmentPopoutBuffers({
          fleet: params.fleet as number,
          startTime: params.startTime,
          endTime: params.endTime,
          wellIds: params.wellIds,
          token,
        });

        if (cancelled) return;

        setPjrBuffers((previous) => mergeBuffers(previous, buffers));

        if (truncated) {
          console.warn(
            "PJR historical payload was truncated; narrow the window or coarsen resolution.",
          );
        }
      } catch (err) {
        console.error("Failed to load PJR treatment channels:", err);
        if (cancelled) return;
        setPjrError(pjrErrorMessage(err));
      } finally {
        if (!cancelled) {
          setPjrLoading(false);
          setPjrPrefetchComplete(true);
        }
      }
    };

    loadPjrHistorical();

    return () => {
      cancelled = true;
    };
  }, [params, pjrReloadRequest]);

  // PJR live poll. Same cadence as the pump incremental feed, but a different
  // endpoint: /treatment/channels/incremental. Cursor lives in a ref so a tick
  // does not restart the interval.
  useEffect(() => {
    if (!params.realtime || params.mode !== "fleet" || !params.fleet) return;

    const token = getAuthToken();
    if (!token) return;

    pjrCursorRef.current = null;
    pjrPollInFlightRef.current = false;

    const normalizeTimestamp = (value: unknown, fallback: number): number => {
      let timestamp = Number(value ?? fallback);
      if (typeof value === "string") timestamp = new Date(value).getTime();
      if (!Number.isFinite(timestamp)) timestamp = fallback;
      if (timestamp < 1_000_000_000_000) timestamp *= 1000;
      return timestamp;
    };

    const reseedFromHistorical = async (endTime: number) => {
      const { buffers } = await loadTreatmentPopoutBuffers({
        fleet: params.fleet!,
        startTime: params.startTime,
        endTime,
        wellIds: params.wellIds,
        token: token!,
      });
      setPjrBuffers(buffers);
    };

    const applyPjrRows = (rows: TreatmentChannelRow[], maxTs: number): void => {
      if (rows.length === 0) return;

      const windowStart = Math.max(
        params.startTime || 0,
        maxTs - STREAMING_WINDOW_MS,
      );

      const incomingByWell = new Map<
        string,
        Map<string, Array<[number, number]>>
      >();

      rows.forEach((row) => {
        const rowTime = normalizeTimestamp(row.time_stamp, maxTs);
        if (rowTime < windowStart || rowTime > maxTs + 5_000) return;

        const wellKey = wellKeyForTreatmentRow(row);
        let channelMap = incomingByWell.get(wellKey);
        if (!channelMap) {
          channelMap = new Map();
          incomingByWell.set(wellKey, channelMap);
        }

        TREATMENT_POPOUT_CHANNEL_VALUES.forEach((channel) => {
          const raw = row[channel];
          if (raw === null || raw === undefined || raw === "") return;
          const numericValue = Number(raw);
          if (!Number.isFinite(numericValue)) return;
          let samples = channelMap!.get(channel);
          if (!samples) {
            samples = [];
            channelMap!.set(channel, samples);
          }
          samples.push([rowTime, numericValue]);
        });
      });

      if (incomingByWell.size === 0) return;

      setPjrBuffers((previous) => {
        const prev: FleetBuffers = previous || new Map();
        const next: FleetBuffers = new Map();
        const allWells = new Set<string>([
          ...Array.from(prev.keys()),
          ...Array.from(incomingByWell.keys()),
        ]);

        allWells.forEach((wellKey) => {
          const oldChannelMap = prev.get(wellKey) || new Map();
          const incomingChannelMap =
            incomingByWell.get(wellKey) ||
            new Map<string, Array<[number, number]>>();
          const channels = new Set<string>([
            ...Array.from(oldChannelMap.keys()),
            ...Array.from(incomingChannelMap.keys()),
          ]);

          const nextChannelMap = new Map<string, ChannelBuffer>();
          channels.forEach((channel) => {
            const merged = new Map<number, number>();
            const existing = oldChannelMap.get(channel);
            const oldTimes = existing?.t
              ? Array.from(existing.t as ArrayLike<number>)
              : [];
            const oldValues = Array.from(
              (existing?.v ?? []) as ArrayLike<number>,
            );
            for (let i = 0; i < oldTimes.length; i += 1) {
              const time = Number(oldTimes[i]);
              if (time >= windowStart && time <= maxTs) {
                merged.set(time, Number(oldValues[i]));
              }
            }
            (incomingChannelMap.get(channel) || []).forEach(([time, value]) => {
              if (time >= windowStart && time <= maxTs) merged.set(time, value);
            });
            if (merged.size === 0) return;
            const times = Array.from(merged.keys()).sort((a, b) => a - b);
            nextChannelMap.set(channel, {
              t: Float64Array.from(times),
              v: Float32Array.from(
                times.map((time) => merged.get(time) as number),
              ),
            });
          });

          if (nextChannelMap.size > 0) next.set(wellKey, nextChannelMap);
        });

        return next;
      });
    };

    const fetchPjrIncremental = async () => {
      if (pjrPollInFlightRef.current) return;
      pjrPollInFlightRef.current = true;

      try {
        const buildIncrementalQuery = (channels: string) => {
          const query = new URLSearchParams({
            fleets: String(params.fleet),
            channels,
          });
          const since = pjrCursorRef.current;
          if (since) query.append("since", String(since));
          params.wellIds.forEach((wellId) =>
            query.append("well_id", String(wellId)),
          );
          return query;
        };

        const [pjrResponse, netResponse] = await Promise.all([
          axios.get(
            `${backendUrl}/treatment/channels/incremental?${buildIncrementalQuery(
              PJR_CHANNELS_CSV,
            ).toString()}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10_000,
            },
          ),
          axios
            .get(
              `${backendUrl}/treatment/channels/incremental?${buildIncrementalQuery(
                NET_PRESSURE_CHANNEL,
              ).toString()}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10_000,
              },
            )
            .catch((error) => {
              console.error("Net pressure incremental poll failed:", error);
              return null;
            }),
        ]);

        const pjrEntry =
          pjrResponse.data?.[params.fleet!] ??
          pjrResponse.data?.[String(params.fleet)];
        const netEntry =
          netResponse?.data?.[params.fleet!] ??
          netResponse?.data?.[String(params.fleet)];
        if (!pjrEntry && !netEntry) return;

        const maxTsRaw = pjrEntry?.max_time_stamp ?? netEntry?.max_time_stamp;
        if (maxTsRaw === null || maxTsRaw === undefined) return;

        const maxTs = normalizeTimestamp(maxTsRaw, Date.now());
        const rows: TreatmentChannelRow[] = [
          ...(Array.isArray(pjrEntry?.rows) ? pjrEntry.rows : []),
          ...(Array.isArray(netEntry?.rows) ? netEntry.rows : []),
        ];

        const cursor = pjrCursorRef.current;
        if (cursor && maxTs - cursor > PJR_LIVE_LOOKBACK_MS) {
          // Incremental only looks back 10 minutes of data time. Re-seed from
          // historical so the gap is filled rather than appending a detached tail.
          try {
            await reseedFromHistorical(Math.max(maxTs, Date.now()));
          } catch (reseedErr) {
            console.error(
              "PJR historical re-seed after live gap failed:",
              reseedErr,
            );
            applyPjrRows(rows, maxTs);
          }
          pjrCursorRef.current = maxTs;
          return;
        }

        applyPjrRows(rows, maxTs);
        pjrCursorRef.current = maxTs;
      } catch (err) {
        console.error("PJR incremental poll failed:", err);
      } finally {
        pjrPollInFlightRef.current = false;
      }
    };

    void fetchPjrIncremental();
    pjrPollRef.current = window.setInterval(
      fetchPjrIncremental,
      STREAMING_POLL_INTERVAL_MS,
    );

    return () => {
      if (pjrPollRef.current !== null) {
        window.clearInterval(pjrPollRef.current);
        pjrPollRef.current = null;
      }
      pjrPollInFlightRef.current = false;
    };
  }, [params]);

  // Chemical historical seed. Fired when this pop-out opens — not on Fleet.
  useEffect(() => {
    if (
      params.mode !== "fleet" ||
      !params.fleet ||
      !params.startTime ||
      !params.endTime
    ) {
      setChemicalPrefetchComplete(true);
      setChemicalLoading(false);
      return;
    }

    let cancelled = false;
    const token = getAuthToken();

    const loadChemicalHistorical = async () => {
      setChemicalLoading(true);
      setChemicalError("");

      try {
        const query = new URLSearchParams({
          fleet: String(params.fleet),
          start_time: String(params.startTime),
          end_time: String(params.endTime),
          channels: CHEMICAL_CHANNELS_CSV,
          resolution: "auto",
        });
        params.wellIds.forEach((wellId) =>
          query.append("well_id", String(wellId)),
        );

        const response = await axios.get(
          `${backendUrl}/chemical/channels?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000,
          },
        );

        if (cancelled) return;

        const rows: TreatmentChannelRow[] = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const parsed = chemicalRowsToBuffers(rows);
        setChemicalBuffers((previous) => mergeBuffers(previous, parsed));

        if (response.data?.truncated) {
          console.warn(
            "Chemical historical payload was truncated; narrow the window or coarsen resolution.",
          );
        }
      } catch (err) {
        console.error("Failed to load chemical channels:", err);
        if (cancelled) return;
        setChemicalError(chemicalErrorMessage(err));
      } finally {
        if (!cancelled) {
          setChemicalLoading(false);
          setChemicalPrefetchComplete(true);
        }
      }
    };

    loadChemicalHistorical();

    return () => {
      cancelled = true;
    };
  }, [params, chemicalReloadRequest]);

  useEffect(() => {
    if (!params.realtime || params.mode !== "fleet" || !params.fleet) return;

    const token = getAuthToken();
    if (!token) return;

    chemicalCursorRef.current = null;
    chemicalPollInFlightRef.current = false;

    const normalizeTimestamp = (value: unknown, fallback: number): number => {
      let timestamp = Number(value ?? fallback);
      if (typeof value === "string") timestamp = new Date(value).getTime();
      if (!Number.isFinite(timestamp)) timestamp = fallback;
      if (timestamp < 1_000_000_000_000) timestamp *= 1000;
      return timestamp;
    };

    const reseedFromHistorical = async (endTime: number) => {
      const query = new URLSearchParams({
        fleet: String(params.fleet),
        start_time: String(params.startTime),
        end_time: String(endTime),
        channels: CHEMICAL_CHANNELS_CSV,
        resolution: "auto",
      });
      params.wellIds.forEach((wellId) =>
        query.append("well_id", String(wellId)),
      );

      const response = await axios.get(
        `${backendUrl}/chemical/channels?${query.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000,
        },
      );

      const rows: TreatmentChannelRow[] = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setChemicalBuffers(chemicalRowsToBuffers(rows));
    };

    const applyChemicalRows = (
      rows: TreatmentChannelRow[],
      maxTs: number,
    ): void => {
      if (rows.length === 0) return;

      const windowStart = Math.max(
        params.startTime || 0,
        maxTs - STREAMING_WINDOW_MS,
      );

      const incomingByName = new Map<
        string,
        Map<string, Array<[number, number]>>
      >();

      rows.forEach((row) => {
        const rowTime = normalizeTimestamp(row.time_stamp, maxTs);
        if (rowTime < windowStart || rowTime > maxTs + 5_000) return;

        const nameKey = chemicalKeyForRow(row);
        let channelMap = incomingByName.get(nameKey);
        if (!channelMap) {
          channelMap = new Map();
          incomingByName.set(nameKey, channelMap);
        }

        CHEMICAL_CHANNEL_VALUES.forEach((channel) => {
          const raw = row[channel];
          if (raw === null || raw === undefined || raw === "") return;
          const numericValue = Number(raw);
          if (!Number.isFinite(numericValue)) return;
          let samples = channelMap!.get(channel);
          if (!samples) {
            samples = [];
            channelMap!.set(channel, samples);
          }
          samples.push([rowTime, numericValue]);
        });
      });

      if (incomingByName.size === 0) return;

      setChemicalBuffers((previous) => {
        const prev: FleetBuffers = previous || new Map();
        const next: FleetBuffers = new Map();
        const allNames = new Set<string>([
          ...Array.from(prev.keys()),
          ...Array.from(incomingByName.keys()),
        ]);

        allNames.forEach((nameKey) => {
          const oldChannelMap = prev.get(nameKey) || new Map();
          const incomingChannelMap =
            incomingByName.get(nameKey) ||
            new Map<string, Array<[number, number]>>();
          const channels = new Set<string>([
            ...Array.from(oldChannelMap.keys()),
            ...Array.from(incomingChannelMap.keys()),
          ]);

          const nextChannelMap = new Map<string, ChannelBuffer>();
          channels.forEach((channel) => {
            const merged = new Map<number, number>();
            const existing = oldChannelMap.get(channel);
            const oldTimes = existing?.t
              ? Array.from(existing.t as ArrayLike<number>)
              : [];
            const oldValues = Array.from(
              (existing?.v ?? []) as ArrayLike<number>,
            );
            for (let i = 0; i < oldTimes.length; i += 1) {
              const time = Number(oldTimes[i]);
              if (time >= windowStart && time <= maxTs) {
                merged.set(time, Number(oldValues[i]));
              }
            }
            (incomingChannelMap.get(channel) || []).forEach(([time, value]) => {
              if (time >= windowStart && time <= maxTs) merged.set(time, value);
            });
            if (merged.size === 0) return;
            const times = Array.from(merged.keys()).sort((a, b) => a - b);
            nextChannelMap.set(channel, {
              t: Float64Array.from(times),
              v: Float32Array.from(
                times.map((time) => merged.get(time) as number),
              ),
            });
          });

          if (nextChannelMap.size > 0) next.set(nameKey, nextChannelMap);
        });

        return next;
      });
    };

    const fetchChemicalIncremental = async () => {
      if (chemicalPollInFlightRef.current) return;
      chemicalPollInFlightRef.current = true;

      try {
        const query = new URLSearchParams({
          fleets: String(params.fleet),
          channels: CHEMICAL_CHANNELS_CSV,
        });
        const since = chemicalCursorRef.current;
        if (since) query.append("since", String(since));
        params.wellIds.forEach((wellId) =>
          query.append("well_id", String(wellId)),
        );

        const response = await axios.get(
          `${backendUrl}/chemical/channels/incremental?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10_000,
          },
        );

        const entry =
          response.data?.[params.fleet!] ??
          response.data?.[String(params.fleet)];
        if (!entry) return;

        const maxTsRaw = entry.max_time_stamp;
        if (maxTsRaw === null || maxTsRaw === undefined) return;

        const maxTs = normalizeTimestamp(maxTsRaw, Date.now());
        const rows: TreatmentChannelRow[] = Array.isArray(entry.rows)
          ? entry.rows
          : [];

        const cursor = chemicalCursorRef.current;
        if (cursor && maxTs - cursor > PJR_LIVE_LOOKBACK_MS) {
          try {
            await reseedFromHistorical(Math.max(maxTs, Date.now()));
          } catch (reseedErr) {
            console.error(
              "Chemical historical re-seed after live gap failed:",
              reseedErr,
            );
            applyChemicalRows(rows, maxTs);
          }
          chemicalCursorRef.current = maxTs;
          return;
        }

        applyChemicalRows(rows, maxTs);
        chemicalCursorRef.current = maxTs;
      } catch (err) {
        console.error("Chemical incremental poll failed:", err);
      } finally {
        chemicalPollInFlightRef.current = false;
      }
    };

    void fetchChemicalIncremental();
    chemicalPollRef.current = window.setInterval(
      fetchChemicalIncremental,
      STREAMING_POLL_INTERVAL_MS,
    );

    return () => {
      if (chemicalPollRef.current !== null) {
        window.clearInterval(chemicalPollRef.current);
        chemicalPollRef.current = null;
      }
      chemicalPollInFlightRef.current = false;
    };
  }, [params]);

  useEffect(() => {
    const loadAlarms = async () => {
      try {
        const alarmPumpNames =
          params.mode === "asset" ? params.assetPumpNames : params.pumps;

        if (!alarmPumpNames.length || !params.startTime || !params.endTime) {
          setAlarmEvents([]);
          return;
        }

        const token = getAuthToken();

        const response = await axios.post(
          `${backendUrl}/pump-alarms`,
          {
            pump_names:
              params.mode === "asset" ? params.assetPumpNames : params.pumps,
            start_time: formatLocalDateTimeForApi(new Date(params.startTime)),
            end_time: formatLocalDateTimeForApi(new Date(params.endTime)),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000,
          },
        );

        setAlarmEvents(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to load popout alarms:", err);
        setAlarmEvents([]);
      }
    };

    loadAlarms();
  }, [params]);
  const filteredAlarmEvents = useMemo(
    () =>
      alarmEvents.filter(
        (alarm) => normalizeAlarmSeverity(alarm) === alarmSeverityFilter,
      ),
    [alarmEvents, alarmSeverityFilter],
  );
  const getAlarmMarkers = useMemo(() => {
    const normalizePumpName = (value: unknown) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const activePumpSet = new Set(
      (params.mode === "asset" ? params.assetPumpNames : params.pumps).map(
        normalizePumpName,
      ),
    );
    const markersMap: Map<number, AlarmEvent[]> = new Map();

    const normalizeAlarmTimestamp = (value: unknown): number | null => {
      if (value === null || value === undefined || value === "") return null;

      let timeMs: number;

      if (typeof value === "number") {
        timeMs = value;
      } else {
        const numericValue = Number(value);
        timeMs =
          Number.isFinite(numericValue) && String(value).trim() !== ""
            ? numericValue
            : new Date(String(value)).getTime();
      }

      if (!Number.isFinite(timeMs)) return null;

      // Some alarm responses use Unix seconds while chart timestamps use
      // milliseconds. Normalize both forms before positioning the marker.
      if (timeMs < 1_000_000_000_000) timeMs *= 1000;

      return timeMs;
    };

    filteredAlarmEvents.forEach((alarm) => {
      const alarmPump = String(
        alarm.pump || alarm.source || alarm.pump_name || "",
      ).trim();
      const alarmTime = alarm.alarm_time || alarm.time_stamp || alarm.timestamp;
      const timeMs = normalizeAlarmTimestamp(alarmTime);

      if (!alarmPump || timeMs === null) return;

      // If the URL contains a pump subset, filter against it. When no pump list
      // is provided, allow all alarm pumps returned by the API.
      if (
        activePumpSet.size > 0 &&
        !activePumpSet.has(normalizePumpName(alarmPump))
      ) {
        return;
      }

      if (!markersMap.has(timeMs)) {
        markersMap.set(timeMs, []);
      }

      markersMap.get(timeMs)!.push({
        ...alarm,
        pump: alarmPump,
        alarm_time: alarmTime,
      });
    });

    return Array.from(markersMap.entries()).sort((a, b) => a[0] - b[0]);
  }, [filteredAlarmEvents, params.mode, params.assetPumpNames, params.pumps]);

  const renderAlarmMarkers = (chart: Highcharts.Chart) => {
    const chartWithMarkers = chart as Highcharts.Chart & {
      customAlarmMarkers?: Highcharts.SVGElement[];
      customAlarmTooltips?: Highcharts.SVGElement[];
    };

    chartWithMarkers.customAlarmMarkers?.forEach((marker) => marker.destroy());
    chartWithMarkers.customAlarmMarkers = [];

    chartWithMarkers.customAlarmTooltips?.forEach((tooltip) =>
      tooltip.destroy(),
    );
    chartWithMarkers.customAlarmTooltips = [];

    const xAxis = chart.xAxis?.[0];
    if (!xAxis || getAlarmMarkers.length === 0) return;

    const destroyAlarmTooltips = () => {
      chartWithMarkers.customAlarmTooltips?.forEach((tooltip) =>
        tooltip.destroy(),
      );
      chartWithMarkers.customAlarmTooltips = [];
      chart.tooltip?.hide(0);
    };

    getAlarmMarkers.forEach(([alarmTimeMs, alarms]) => {
      const x = xAxis.toPixels(alarmTimeMs, false);

      if (!Number.isFinite(x)) return;
      if (x < chart.plotLeft || x > chart.plotLeft + chart.plotWidth) return;

      const severity = getHighestAlarmSeverity(alarms);
      const severityMeta = getAlarmSeverityMeta(severity);

      const markerSize = 14;
      const markerCenterY = chart.plotTop + 15;
      const markerX = x - markerSize / 2;
      const markerY = markerCenterY - markerSize / 2;

      const openAlarmModal = () => {
        destroyAlarmTooltips();
        setSelectedAlarmEvents(alarms);
        setShowAlarmModal(true);
      };

      const showAlarmTooltip = () => {
        destroyAlarmTooltips();

        const tooltipX = Math.min(
          chart.chartWidth - 250,
          Math.max(chart.plotLeft, x + 12),
        );
        const tooltipY = markerCenterY + 16;

        const tooltipLabel = chart.renderer
          .label(
            `<b>${severityMeta.label} Alarms</b><br/>${alarms.length} alarm(s)<br/>Click to view details`,
            tooltipX,
            tooltipY,
            "callout",
            x,
            markerCenterY,
            true,
          )
          .attr({
            fill: "rgba(48, 51, 53, 0.97)",
            stroke: "#5C646B",
            "stroke-width": 1,
            padding: 8,
            zIndex: 10010,
          })
          .css({
            color: "#ffffff",
            fontSize: "12px",
            pointerEvents: "none",
          } as any)
          .add();

        chartWithMarkers.customAlarmTooltips = [tooltipLabel];
      };

      const halfWidth = markerSize / 2;
      const quarterWidth = markerSize / 4;
      const halfHeight = markerSize / 2;

      const stopShape = chart.renderer
        .path([
          ["M", x - quarterWidth, markerCenterY - halfHeight],
          ["L", x + quarterWidth, markerCenterY - halfHeight],
          ["L", x + halfWidth, markerCenterY],
          ["L", x + quarterWidth, markerCenterY + halfHeight],
          ["L", x - quarterWidth, markerCenterY + halfHeight],
          ["L", x - halfWidth, markerCenterY],
          ["Z"],
        ])
        .attr({
          fill: severityMeta.color,
          stroke: severityMeta.color,
          "stroke-width": 1,
          zIndex: 10020,
          cursor: "pointer",
        })
        .add();

      const hitBox = chart.renderer
        .rect(x - 13, markerCenterY - 13, 26, 26, 4)
        .attr({
          fill: "rgba(255,255,255,0.001)",
          zIndex: 10021,
          cursor: "pointer",
        })
        .add();

      stopShape.element.classList.add("chart-alarm-marker");
      hitBox.element.classList.add("chart-alarm-marker");

      stopShape.on("click", openAlarmModal);
      hitBox.on("click", openAlarmModal);
      stopShape.on("mouseover", showAlarmTooltip);
      hitBox.on("mouseover", showAlarmTooltip);
      stopShape.on("mouseout", destroyAlarmTooltips);
      hitBox.on("mouseout", destroyAlarmTooltips);

      chartWithMarkers.customAlarmMarkers = [
        ...(chartWithMarkers.customAlarmMarkers || []),
        stopShape,
        hitBox,
      ];
    });
  };
  const bindEditableYAxisLabels = useCallback(
    (chart: Highcharts.Chart) => {
      const container = chart.container as HTMLElement & {
        __padTrackAxisDblClickBound?: boolean;
      };

      if (!container || container.__padTrackAxisDblClickBound) return;

      container.__padTrackAxisDblClickBound = true;

      const markEditableLabels = () => {
        (chart.yAxis || []).forEach((axis: any) => {
          Object.values((axis as any).ticks || {}).forEach((tick: any) => {
            const labelElement = tick?.label?.element as SVGElement | undefined;
            if (!labelElement) return;

            labelElement.setAttribute("cursor", "pointer");
            labelElement.setAttribute(
              "title",
              "Double-click this axis value to rescale.",
            );
          });
        });
      };

      container.addEventListener("dblclick", (event: MouseEvent) => {
        const containerRect = container.getBoundingClientRect();
        const chartX = event.clientX - containerRect.left;
        const chartY = event.clientY - containerRect.top;

        let matchedAxis: Highcharts.Axis | null = null;
        let bestDistance = Number.POSITIVE_INFINITY;

        (chart.yAxis || []).forEach((axis: any) => {
          if (!axis.visible || axis.options?.custom?.editable === false) return;

          const axisLeft = Number(axis.left ?? chart.plotLeft);
          const axisTop = Number(axis.top ?? chart.plotTop);
          const axisHeight = Number(axis.height ?? chart.plotHeight);
          const axisWidth = Number(axis.width ?? chart.plotWidth);
          const axisRight = axisLeft + axisWidth;
          const isLeftSide = axis.opposite !== true;

          const labelZoneLeft = isLeftSide ? axisLeft - 120 : axisRight - 20;
          const labelZoneRight = isLeftSide ? axisLeft + 35 : axisRight + 120;
          const labelZoneTop = axisTop - 25;
          const labelZoneBottom = axisTop + axisHeight + 25;

          const insideLabelZone =
            chartX >= labelZoneLeft &&
            chartX <= labelZoneRight &&
            chartY >= labelZoneTop &&
            chartY <= labelZoneBottom;

          if (!insideLabelZone) return;

          const axisEdgeX = isLeftSide ? axisLeft : axisRight;
          const distance = Math.abs(chartX - axisEdgeX);

          if (distance < bestDistance) {
            bestDistance = distance;
            matchedAxis = axis as Highcharts.Axis;
          }
        });

        if (!matchedAxis) return;

        event.preventDefault();
        event.stopPropagation();

        const axis = matchedAxis as Highcharts.Axis;
        const extremes = axis.getExtremes();

        if (
          !Number.isFinite(extremes.min) ||
          !Number.isFinite(extremes.max) ||
          extremes.max <= extremes.min
        ) {
          return;
        }

        const axisTop = Number((axis as any).top ?? chart.plotTop);
        const axisHeight = Number((axis as any).height ?? chart.plotHeight);
        const clampedY = Math.min(
          axisTop + axisHeight,
          Math.max(axisTop, chartY),
        );

        const ratioFromBottom = (axisTop + axisHeight - clampedY) / axisHeight;
        const currentRange = extremes.max - extremes.min;
        const clickedValue = extremes.min + ratioFromBottom * currentRange;

        openAxisValueEditor({
          axis,
          axisTitle:
            (axis.options?.title as any)?.text ||
            (axis as any).coll ||
            "Y Axis",
          clickedValue,
          ratioFromBottom,
          currentMin: extremes.min,
          currentMax: extremes.max,
        });
      });

      markEditableLabels();
      setTimeout(markEditableLabels, 0);
    },
    [openAxisValueEditor],
  );

  const handleChartCreated = useCallback(
    (chart: Highcharts.Chart) => {
      chartInstanceRef.current = chart;
      bindEditableYAxisLabels(chart);
    },
    [bindEditableYAxisLabels],
  );

  const activeChannelValue =
    activeSpecialTabValue ||
    selectedChannelValues[0] ||
    params.channels[0] ||
    "rpm";
  const isPjrTab = activeChannelValue === PJR_TAB_VALUE;
  const isChemRateTab = activeChannelValue === CHEM_RATE_TAB_VALUE;
  const isChemRatioTab = activeChannelValue === CHEM_RATIO_TAB_VALUE;
  const isNetPressureTab = activeChannelValue === NET_PRESSURE_TAB_VALUE;
  const isChemicalTab = isChemRateTab || isChemRatioTab;
  const isPowerEndTemperatureTab =
    isPowerEndTemperatureValue(activeChannelValue);
  const hasPowerEndTemperatureSelection = selectedChannels.some((channel) =>
    isPowerEndTemperatureValue(channel.value),
  );
  const isSpecialTab = isPjrTab || isChemicalTab || isNetPressureTab;
  const activeChannelLabel = isPjrTab
    ? "PJR"
    : isChemRateTab
      ? "Chemical (Rate)"
      : isChemRatioTab
        ? "Chemical (Ratio)"
        : isNetPressureTab
          ? "Net Pressure"
          : selectedChannels.length > 1
            ? `${selectedChannels.length} selected channels`
            : isPowerEndTemperatureTab
              ? "Power End Temperature"
              : selectedChannels[0]?.label || "selected channel";
  const pjrHasData = buffersHaveChannel(pjrBuffers);
  const netPressureHasData = buffersHaveChannel(pjrBuffers, "net_pressure");
  const chemicalRateHasData = buffersHaveChannel(
    chemicalBuffers,
    "chemical_rate",
  );
  const chemicalRatioHasData = buffersHaveChannel(
    chemicalBuffers,
    "chemical_ratio",
  );
  const hasPumpChartData = Boolean(
    buffers &&
    selectedChannels.length > 0 &&
    Array.from(buffers.values()).some((channelMap) =>
      selectedChannels.some((channel) => {
        const channelBuffer = isPowerEndTemperatureValue(channel.value)
          ? powerEndTemperatureBuffer(channelMap, channel.value)
          : channelMap.get(channel.value);
        return Boolean(channelBuffer && channelBuffer.t.length > 0);
      }),
    ),
  );
  const hasChartData = isPjrTab
    ? pjrHasData
    : isChemRateTab
      ? chemicalRateHasData
      : isChemRatioTab
        ? chemicalRatioHasData
        : isNetPressureTab
          ? netPressureHasData
          : hasPumpChartData;
  const isTabLoading =
    isPjrTab || isNetPressureTab
      ? pjrLoading
      : isChemicalTab
        ? chemicalLoading
        : loading;
  const tabError =
    isPjrTab || isNetPressureTab
      ? pjrError
      : isChemicalTab
        ? chemicalError
        : error;

  // The plot is sized from the flex container rather than the viewport, so it
  // always fills the space left over by the header, tabs, and legend.
  useEffect(() => {
    const element = chartAreaRef.current;
    if (!element) return;

    const measure = () => {
      // clientWidth/Height, not the bounding rect: the container is border-box
      // with a 1px border, and an oversized chart would clip inside it.
      const width = element.clientWidth;
      const height = element.clientHeight;
      setChartSize((previous) =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height },
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // A tab with no data renders no chart, so the stale instance would otherwise
  // linger and be read by the axis-label editor.
  useEffect(() => {
    if (hasChartData) return;
    chartInstanceRef.current = null;
  }, [hasChartData, activeChannelValue]);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const sharedChart: Highcharts.ChartOptions = {
      backgroundColor: "transparent",
      width: chartSize.width,
      height: chartSize.height,
      spacingTop: 16,
      spacingRight: 16,
      spacingBottom: 10,
      marginLeft: CHART_MARGIN_LEFT,
      marginRight: CHART_MARGIN_RIGHT,
      zooming: {
        type: "x",
      },
      events: {
        load: function () {
          renderAlarmMarkers(this as Highcharts.Chart);
        },
        render: function () {
          renderAlarmMarkers(this as Highcharts.Chart);
        },
      },
    };

    // The popout is always dark. The placeholder keeps the yAxis slot after
    // the four channel axes; the grid itself comes from the first visible
    // channel axis (see channelAxisGridOptions).
    const placeholderGridAxis = (enabled: boolean) =>
      ({
        ...emptyChartGridAxis(true, enabled),
        custom: { editable: false },
      }) as Highcharts.YAxisOptions;

    const buildKeyedSingleChannelChart = (
      sourceBuffers: FleetBuffers | null,
      channelValue: string,
      axisTitle: string,
      valueSuffix: string,
      nameForKey: (key: string, keyCount: number) => string,
      colorForKey?: (key: string, index: number) => string | undefined,
    ): Highcharts.Options => {
      const series: Highcharts.SeriesOptionsType[] = [];
      const keys = sourceBuffers ? Array.from(sourceBuffers.keys()).sort() : [];

      keys.forEach((rowKey, index) => {
        const buf = sourceBuffers?.get(rowKey)?.get(channelValue);
        if (!buf || buf.t.length === 0) return;
        series.push({
          type: "line",
          name: nameForKey(rowKey, keys.length),
          data: minMaxDownsample(buf, DEFAULT_MAX_POINTS),
          color: colorForKey?.(rowKey, index),
          connectNulls: true,
          animation: false,
          marker: { enabled: false },
          turboThreshold: 0,
          yAxis: 0,
          tooltip: {
            valueDecimals: 2,
            valueSuffix,
          },
        } as Highcharts.SeriesLineOptions);
      });

      return {
        chart: sharedChart,
        title: { text: undefined },
        xAxis: {
          type: "datetime",
          title: {
            text: "Time",
            style: { color: "#ffffff" },
          },
          labels: {
            style: { color: "#ffffff" },
          },
          events: {
            setExtremes: function (
              this: Highcharts.Axis,
              e: Highcharts.AxisSetExtremesEventObject,
            ) {
              const chart = this.chart;
              if (!chart || !sourceBuffers) return;

              const hasWindow =
                typeof e.min === "number" && typeof e.max === "number";
              const byName = new Map<string, ChartPoint[]>();
              const seriesKeys = Array.from(sourceBuffers.keys());

              sourceBuffers.forEach((channelMap, rowKey) => {
                const buf = channelMap.get(channelValue);
                if (!buf || buf.t.length === 0) return;
                const windowedBuf = hasWindow
                  ? sliceBuffer(buf, e.min as number, e.max as number)
                  : buf;
                byName.set(
                  nameForKey(rowKey, seriesKeys.length),
                  minMaxDownsample(windowedBuf, DEFAULT_MAX_POINTS),
                );
              });

              chart.series.forEach((s) => {
                const d = byName.get(s.name as string);
                if (d) s.setData(d, false);
              });
              chart.redraw();
            },
          },
        },
        yAxis: [
          {
            custom: { editable: true },
            visible: true,
            opposite: false,
            title: {
              text: axisTitle,
              rotation: 270,
              margin: 14,
              style: { color: "#ffffff", fontSize: "10px" },
            },
            labels: {
              enabled: true,
              reserveSpace: true,
              x: -6,
              style: { color: "#ffffff", fontSize: "10px" },
            },
            lineWidth: 1,
            tickWidth: 1,
            ...channelAxisGridOptions({
              drawsGrid: true,
              channelValues: [channelValue],
              isDarkTheme: true,
            }),
          } as Highcharts.YAxisOptions,
          {
            custom: { editable: false },
            visible: false,
          } as Highcharts.YAxisOptions,
          {
            custom: { editable: false },
            visible: false,
          } as Highcharts.YAxisOptions,
          {
            custom: { editable: false },
            visible: false,
          } as Highcharts.YAxisOptions,
          placeholderGridAxis(series.length === 0),
        ],
        legend: POPOUT_LEGEND,
        tooltip: ALL_SERIES_TOOLTIP,
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            connectNulls: true,
            animation: false,
          },
        },
        series,
      };
    };

    if (isPjrTab) {
      const series: Highcharts.SeriesOptionsType[] = [];
      const wellKeys = pjrBuffers ? Array.from(pjrBuffers.keys()).sort() : [];

      wellKeys.forEach((wellKey) => {
        const channelMap = pjrBuffers?.get(wellKey);
        if (!channelMap) return;

        PJR_CHANNEL_DEFS.forEach((channel) => {
          const buf = channelMap.get(channel.value);
          if (!buf || buf.t.length === 0) return;
          const downsampledPoints = minMaxDownsample(buf, DEFAULT_MAX_POINTS);

          series.push({
            type: "line",
            name: pjrSeriesName(
              channel,
              wellKey,
              wellKeys.length,
              params.wellNamesById[wellKey],
            ),
            data: downsampledPoints,
            color: channel.color,
            connectNulls: true,
            animation: false,
            marker: { enabled: false },
            turboThreshold: 0,
            yAxis: AXIS_INDEX[channel.axis],
            tooltip: {
              valueDecimals: 2,
              valueSuffix: ` ${channel.unit}`,
            },
          } as Highcharts.SeriesLineOptions);
        });
      });

      const pjrAxisTitle: Record<AxisName, string> = {
        left1: "Pressure (psi)",
        left2: "",
        right1: "Rate (bpm)",
        right2: "Density (ppa)",
      };

      return {
        chart: sharedChart,
        title: { text: undefined },
        xAxis: {
          type: "datetime",
          title: {
            text: "Time",
            style: { color: "#ffffff" },
          },
          labels: {
            style: { color: "#ffffff" },
          },
          events: {
            setExtremes: function (
              this: Highcharts.Axis,
              e: Highcharts.AxisSetExtremesEventObject,
            ) {
              const chart = this.chart;
              if (!chart || !pjrBuffers) return;

              const hasWindow =
                typeof e.min === "number" && typeof e.max === "number";
              const byName = new Map<string, ChartPoint[]>();
              const keys = Array.from(pjrBuffers.keys());

              pjrBuffers.forEach((channelMap, wellKey) => {
                PJR_CHANNEL_DEFS.forEach((channel) => {
                  const buf = channelMap.get(channel.value);
                  if (!buf || buf.t.length === 0) return;
                  const windowedBuf = hasWindow
                    ? sliceBuffer(buf, e.min as number, e.max as number)
                    : buf;
                  byName.set(
                    pjrSeriesName(
                      channel,
                      wellKey,
                      keys.length,
                      params.wellNamesById[wellKey],
                    ),
                    minMaxDownsample(windowedBuf, DEFAULT_MAX_POINTS),
                  );
                });
              });

              chart.series.forEach((s) => {
                const d = byName.get(s.name as string);
                if (d) s.setData(d, false);
              });
              chart.redraw();
            },
          },
        },
        yAxis: [
          ...(["left1", "left2", "right1", "right2"] as AxisName[]).map(
            (axisName) => {
              const isVisible = axisName !== "left2";
              const opposite = axisName === "right1" || axisName === "right2";

              return {
                custom: { editable: true },
                visible: isVisible,
                opposite,
                offset:
                  axisName === "left2" ? 55 : axisName === "right2" ? 70 : 0,
                title: {
                  text: isVisible ? pjrAxisTitle[axisName] : undefined,
                  rotation: opposite ? 90 : 270,
                  margin: 14,
                  style: { color: "#ffffff", fontSize: "10px" },
                },
                labels: {
                  enabled: isVisible,
                  reserveSpace: isVisible,
                  x: opposite ? 6 : -6,
                  style: { color: "#ffffff", fontSize: "10px" },
                },
                lineWidth: isVisible ? 1 : 0,
                tickWidth: isVisible ? 1 : 0,
                ...channelAxisGridOptions({
                  drawsGrid: axisName === "left1",
                  channelValues: PJR_CHANNEL_DEFS.filter(
                    (channel) => channel.axis === axisName,
                  ).map((channel) => channel.value),
                  isDarkTheme: true,
                }),
              } as Highcharts.YAxisOptions;
            },
          ),
          placeholderGridAxis(series.length === 0),
        ],
        legend: POPOUT_LEGEND,
        tooltip: ALL_SERIES_TOOLTIP,
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            connectNulls: true,
            animation: false,
          },
        },
        series,
      };
    }

    if (isNetPressureTab) {
      return buildKeyedSingleChannelChart(
        pjrBuffers,
        "net_pressure",
        "Net Pressure (psi)",
        " psi",
        (wellKey, count) =>
          count <= 1 || wellKey === "fleet"
            ? "Net Pressure (psi)"
            : `Net Pressure (psi) · ${params.wellNamesById[wellKey] || `Well ${wellKey}`}`,
        () => "#22d3ee",
      );
    }

    if (isChemicalTab) {
      const channelValue = isChemRateTab ? "chemical_rate" : "chemical_ratio";
      const axisTitle = isChemRateTab ? "Chemical Rate" : "Chemical Ratio";
      return buildKeyedSingleChannelChart(
        chemicalBuffers,
        channelValue,
        axisTitle,
        "",
        (name) => name,
        (_key, index) =>
          CHEMICAL_SERIES_COLORS[index % CHEMICAL_SERIES_COLORS.length],
      );
    }

    const series: Highcharts.SeriesOptionsType[] = [];

    const pumps =
      params.mode === "asset"
        ? buffers
          ? Array.from(buffers.keys()).sort()
          : params.assetPumpNames
        : params.pumps.length > 0
          ? params.pumps
          : buffers
            ? Array.from(buffers.keys()).sort()
            : [];

    const hasRealTemperatureData = (
      buffer: ChannelBuffer | undefined,
    ): boolean => {
      if (!buffer || buffer.t.length === 0) return false;

      return Array.from(buffer.v).some(
        (value) => Number.isFinite(Number(value)) && Number(value) !== 0,
      );
    };
    const channelsForPump = (
      chMap: Map<string, ChannelBuffer>,
    ): PumpChannel[] => {
      if (!hasPowerEndTemperatureSelection) return selectedChannels;

      const genericChannel = POWER_END_TEMPERATURE_CHANNELS[0];
      const inletChannel = POWER_END_TEMPERATURE_CHANNELS[1];
      const outletChannel = POWER_END_TEMPERATURE_CHANNELS[2];

      const genericBuffer = powerEndTemperatureBuffer(
        chMap,
        genericChannel.value,
      );
      const inletBuffer = powerEndTemperatureBuffer(chMap, inletChannel.value);
      const outletBuffer = powerEndTemperatureBuffer(
        chMap,
        outletChannel.value,
      );

      const nonPowerEndChannels = selectedChannels.filter(
        (channel) => !isPowerEndTemperatureValue(channel.value),
      );

      const hasInletTemperature =
        hasRealTemperatureData(inletBuffer) ||
        hasRealTemperatureData(genericBuffer);

      const hasOutletTemperature = hasRealTemperatureData(outletBuffer);

      const powerEndChannels: PumpChannel[] = hasOutletTemperature
        ? [...(hasInletTemperature ? [inletChannel] : []), outletChannel]
        : hasInletTemperature
          ? [inletChannel]
          : [];

      return [...nonPowerEndChannels, ...powerEndChannels];
    };

    const seriesNameFor = (pumpName: string, channel: PumpChannel) =>
      selectedChannels.length > 1 || hasPowerEndTemperatureSelection
        ? `${pumpName} - ${channel.label}`
        : pumpName;

    pumps.forEach((pumpName) => {
      const chMap = buffers?.get(pumpName);
      if (!chMap) return;

      channelsForPump(chMap).forEach((channel) => {
        const buf = isPowerEndTemperatureValue(channel.value)
          ? powerEndTemperatureBuffer(chMap, channel.value)
          : chMap.get(channel.value);
        if (!buf || buf.t.length === 0) return;
        const downsampledPoints = minMaxDownsample(buf, DEFAULT_MAX_POINTS);

        series.push({
          type: "line",
          name: seriesNameFor(pumpName, channel),
          data: downsampledPoints,
          connectNulls: true,
          marker: { enabled: false },
          turboThreshold: 0,
          yAxis: ["left1", "left2", "right1", "right2"].indexOf(
            getAxisNameForChannel(channel.value),
          ),
          tooltip: isPowerEndTemperatureValue(channel.value)
            ? {
                valueDecimals: 2,
                valueSuffix: " °F",
              }
            : undefined,
        } as Highcharts.SeriesLineOptions);
      });
    });

    const pumpAxisNames: AxisName[] = ["left1", "left2", "right1", "right2"];
    const activeChannelsForAxis = (axisName: AxisName): PumpChannel[] =>
      selectedChannels.length === 1
        ? axisName === "left1"
          ? selectedChannels
          : []
        : selectedChannels.filter(
            (channel) => getAxisNameForChannel(channel.value) === axisName,
          );
    // Only the first visible axis draws grid lines; the others are
    // tick-aligned to it.
    const pumpGridAxisName = pumpAxisNames.find(
      (axisName) => activeChannelsForAxis(axisName).length > 0,
    );

    return {
      chart: sharedChart,

      title: {
        text: undefined,
      },
      xAxis: {
        type: "datetime",
        title: {
          text: "Time",
          style: { color: "#ffffff" },
        },
        labels: {
          style: { color: "#ffffff" },
        },
        events: {
          setExtremes: function (
            this: Highcharts.Axis,
            e: Highcharts.AxisSetExtremesEventObject,
          ) {
            const chart = this.chart;
            if (!chart || !buffers) return;

            const hasWindow =
              typeof e.min === "number" && typeof e.max === "number";
            const byName = new Map<string, ChartPoint[]>();
            buffers.forEach((chMap, pumpName) => {
              channelsForPump(chMap).forEach((channel) => {
                const buf = isPowerEndTemperatureValue(channel.value)
                  ? powerEndTemperatureBuffer(chMap, channel.value)
                  : chMap.get(channel.value);
                if (!buf || buf.t.length === 0) return;
                const windowedBuf = hasWindow
                  ? sliceBuffer(buf, e.min as number, e.max as number)
                  : buf;
                const downsampledPoints = minMaxDownsample(
                  windowedBuf,
                  DEFAULT_MAX_POINTS,
                );

                byName.set(seriesNameFor(pumpName, channel), downsampledPoints);
              });
            });
            chart.series.forEach((s) => {
              const d = byName.get(s.name as string);
              if (d) s.setData(d, false);
            });
            chart.redraw();
          },
        },
      },
      yAxis: [
        ...pumpAxisNames.map(
          (axisName) => {
            const activeChannels = activeChannelsForAxis(axisName);
            const isVisible = activeChannels.length > 0;
            const opposite = axisName === "right1" || axisName === "right2";

            const isOriginalOpenedChannel =
              activeChannelValue === (params.channels[0] || "rpm");
            const handedExtremes = isOriginalOpenedChannel
              ? popoutAxisExtremes[axisName] || {}
              : {};
            const settings = popoutAxisSettings[axisName] || {
              channels: [],
            };
            const useAutoScale =
              !isOriginalOpenedChannel ||
              popoutAxisAutoScale[axisName] !== false;
            const axisContainsOnlyPowerEndTemperature =
              activeChannels.length > 0 &&
              activeChannels.every((channel) =>
                isPowerEndTemperatureValue(channel.value),
              );
            const titleText = axisContainsOnlyPowerEndTemperature
              ? "Power End Temperature (°F)"
              : activeChannels.length === 0
                ? undefined
                : activeChannels.length === 1
                  ? activeChannels[0].label
                  : `${activeChannels[0].label} +${activeChannels.length - 1}`;

            return {
              custom: { editable: true },
              visible: isVisible,
              opposite,
              offset:
                axisName === "left2" ? 55 : axisName === "right2" ? 70 : 0,
              min: useAutoScale
                ? handedExtremes.min
                : (settings.min ?? handedExtremes.min),
              max: useAutoScale
                ? handedExtremes.max
                : (settings.max ?? handedExtremes.max),
              title: {
                text: titleText,
                rotation: opposite ? 90 : 270,
                margin: 14,
                style: { color: "#ffffff", fontSize: "10px" },
              },
              labels: {
                enabled: isVisible,
                reserveSpace: isVisible,
                x: opposite ? 6 : -6,
                style: { color: "#ffffff", fontSize: "10px" },
              },
              lineWidth: isVisible ? 1 : 0,
              tickWidth: isVisible ? 1 : 0,
              ...channelAxisGridOptions({
                drawsGrid: axisName === pumpGridAxisName,
                channelValues: activeChannels.map((channel) => channel.value),
                isDarkTheme: true,
              }),
            } as Highcharts.YAxisOptions;
          },
        ),
        placeholderGridAxis(series.length === 0 || !pumpGridAxisName),
      ],
      legend: POPOUT_LEGEND,
      tooltip: ALL_SERIES_TOOLTIP,
      credits: {
        enabled: false,
      },

      plotOptions: {
        series: {
          connectNulls: true,
        },
      },

      series,
    };
  }, [
    buffers,
    pjrBuffers,
    chemicalBuffers,
    isPjrTab,
    isChemicalTab,
    isChemRateTab,
    isNetPressureTab,
    isPowerEndTemperatureTab,
    hasPowerEndTemperatureSelection,
    params,
    selectedChannels,
    selectedChannelValues,
    getAlarmMarkers,
    getAxisNameForChannel,
    popoutAxisSettings,
    popoutAxisAutoScale,
    popoutAxisExtremes,
    chartSize,
  ]);

  const heading = useMemo(() => {
    if (params.mode === "asset") {
      return {
        badge: "Asset",
        primary: params.assetPumpNames.join(", "),
        wells: [] as string[],
      };
    }

    return {
      badge: params.fleet != null ? `Fleet ${params.fleet}` : "Fleet",
      primary: params.padName,
      wells: params.wellNames,
    };
  }, [
    params.assetPumpNames,
    params.fleet,
    params.mode,
    params.padName,
    params.wellNames,
  ]);

  const headingTooltip = [heading.badge, heading.primary, ...heading.wells]
    .filter(Boolean)
    .join(" · ");

  const returnToPumpData = () => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        return;
      }
    } catch {}

    window.location.assign(
      params.mode === "asset" ? "/fleet" : `/fleet?fleet=${params.fleet}`,
    );
  };

  const openChannelEditor = () => {
    const currentPumpChannels = selectedChannelValues.filter(
      (value) => !SPECIAL_POPOUT_CHANNEL_VALUES.has(value),
    );
    const fallbackChannels =
      params.channels.length > 0 ? params.channels : ["rpm"];

    setDraftChannelValues(
      currentPumpChannels.length > 0 ? currentPumpChannels : fallbackChannels,
    );
    setChannelSearch("");
    setShowChannelEditor(true);
  };

  const toggleDraftChannel = (channelValue: string) => {
    setDraftChannelValues((previous) =>
      previous.includes(channelValue)
        ? previous.filter((value) => value !== channelValue)
        : [...previous, channelValue],
    );
  };

  const applyChannelEdits = () => {
    const nextChannels = Array.from(
      new Set(draftChannelValues.filter(Boolean)),
    );
    if (nextChannels.length === 0) return;

    setPopoutAxisAutoScale({
      left1: true,
      left2: true,
      right1: true,
      right2: true,
    });
    setPopoutAxisExtremes({
      left1: {},
      left2: {},
      right1: {},
      right2: {},
    });
    // Applying an edit always returns to the edited pump-channel chart. A
    // previously-open PJR/Chemical/Net Pressure tab must not remain active.
    setActiveSpecialTabValue(null);
    setSelectedChannelValues(nextChannels);
    setShowChannelEditor(false);

    const search = new URLSearchParams(window.location.search);
    search.set("channels", nextChannels.join(","));
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${search.toString()}`,
    );
  };

  // Tab switch = pure re-render from the already-prefetched buffers. No fetch,
  // no loading state — only the axis overrides handed over by the opener are
  // dropped, since they belong to the channel the pop-out was opened with.
  const selectChannelTab = (channelValue: string) => {
    if (channelValue === activeChannelValue) return;

    setPopoutAxisAutoScale({
      left1: true,
      left2: true,
      right1: true,
      right2: true,
    });
    setPopoutAxisExtremes({
      left1: {},
      left2: {},
      right1: {},
      right2: {},
    });

    if (SPECIAL_POPOUT_CHANNEL_VALUES.has(channelValue)) {
      // Special tabs are separate views. Do not replace the editable pump
      // channels, otherwise Edit Channels would suddenly show 0 selected and
      // the user's RPM + added-channel combination would be lost.
      setActiveSpecialTabValue(channelValue);
      return;
    }

    setActiveSpecialTabValue(null);
    setSelectedChannelValues([channelValue]);
  };

  return (
    <div className="chart-popout-page">
      <header className="chart-popout-header">
        <div className="chart-popout-title" title={headingTooltip}>
          <span className="chart-popout-fleet-chip">{heading.badge}</span>
          {heading.primary && (
            <span className="chart-popout-pad">{heading.primary}</span>
          )}
          {heading.wells.length > 0 && (
            <>
              <span className="chart-popout-divider" />
              <span className="chart-popout-wells">
                {heading.wells.map((wellName) => (
                  <span className="chart-popout-well-chip" key={wellName}>
                    {wellName}
                  </span>
                ))}
              </span>
            </>
          )}
        </div>

        <div className="chart-popout-severity">
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() =>
                showChannelEditor
                  ? setShowChannelEditor(false)
                  : openChannelEditor()
              }
              style={{
                background: showChannelEditor ? "#31451f" : "#2d3339",
                color: "#ffffff",
                border: showChannelEditor
                  ? "1px solid #8cc540"
                  : "1px solid #4b5563",
                borderRadius: 8,
                padding: "6px 14px",
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Add or remove channels without closing this graph"
            >
              Edit Channels ({selectedChannelValues.length}) ▾
            </button>

            {showChannelEditor && (
              <div
                style={{
                  position: "absolute",
                  top: "115%",
                  right: 0,
                  width: 380,
                  maxWidth: "min(380px, 88vw)",
                  background: "#1f252b",
                  border: "1px solid #4b5563",
                  borderRadius: 10,
                  zIndex: 20000,
                  boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: 12, borderBottom: "1px solid #3f474f" }}>
                  <div
                    style={{
                      color: "#ffffff",
                      fontWeight: 800,
                      marginBottom: 8,
                    }}
                  >
                    Edit displayed channels
                  </div>
                  <input
                    type="text"
                    value={channelSearch}
                    onChange={(event) => setChannelSearch(event.target.value)}
                    placeholder="Search channels..."
                    autoFocus
                    style={{
                      width: "100%",
                      height: 36,
                      boxSizing: "border-box",
                      border: "1px solid #5b6168",
                      borderRadius: 7,
                      background: "#2d3339",
                      color: "#ffffff",
                      padding: "0 10px",
                      outline: "none",
                    }}
                  />
                </div>

                <div
                  style={{
                    maxHeight: 330,
                    overflowY: "auto",
                    padding: "6px 0",
                  }}
                >
                  {filteredEditableChannelOptions.map((channel) => {
                    const checked = draftChannelValues.includes(channel.value);
                    return (
                      <label
                        key={channel.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          padding: "8px 12px",
                          cursor: "pointer",
                          color: checked ? "#ffffff" : "#d1d5db",
                          background: checked
                            ? "rgba(140, 197, 64, 0.12)"
                            : "transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDraftChannel(channel.value)}
                          style={{ accentColor: "#8cc540" }}
                        />
                        <span style={{ fontSize: 12.5 }}>{channel.label}</span>
                      </label>
                    );
                  })}

                  {filteredEditableChannelOptions.length === 0 && (
                    <div
                      style={{
                        padding: 16,
                        color: "#9ca3af",
                        fontSize: 12,
                        textAlign: "center",
                      }}
                    >
                      No matching channels.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: 10,
                    borderTop: "1px solid #3f474f",
                    background: "#252b31",
                  }}
                >
                  <span
                    style={{
                      color:
                        draftChannelValues.length > 0 ? "#aeb6bf" : "#fca5a5",
                      fontSize: 11.5,
                    }}
                  >
                    {draftChannelValues.length > 0
                      ? `${draftChannelValues.length} selected`
                      : "Select at least one channel"}
                  </span>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setShowChannelEditor(false)}
                      style={{
                        height: 32,
                        padding: "0 12px",
                        borderRadius: 6,
                        border: "1px solid #5b6168",
                        background: "#30363d",
                        color: "#ffffff",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={applyChannelEdits}
                      disabled={draftChannelValues.length === 0}
                      style={{
                        height: 32,
                        padding: "0 14px",
                        borderRadius: 6,
                        border: "1px solid #8cc540",
                        background:
                          draftChannelValues.length > 0 ? "#6fa92d" : "#46503d",
                        color: "#ffffff",
                        fontWeight: 800,
                        cursor:
                          draftChannelValues.length > 0
                            ? "pointer"
                            : "not-allowed",
                        opacity: draftChannelValues.length > 0 ? 1 : 0.65,
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <span>Alarm Severity:</span>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setShowSeverityMenu((prev) => !prev)}
              style={{
                background: "#2d3339",
                color: "#ffffff",
                border: "1px solid #4b5563",
                borderRadius: 8,
                padding: "6px 14px",
                fontWeight: 700,
                cursor: "pointer",
                minWidth: 105,
              }}
            >
              {alarmSeverityFilter === "critical" ? "Critical" : "Warning"} ▾
            </button>

            {showSeverityMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "115%",
                  right: 0,
                  background: "#1f2933",
                  border: "1px solid #4b5563",
                  borderRadius: 8,
                  overflow: "hidden",
                  zIndex: 10000,
                  minWidth: 120,
                  boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                }}
              >
                {(["critical", "warning"] as const).map((value) => (
                  <div
                    key={value}
                    onClick={() => {
                      setAlarmSeverityFilter(value);
                      setShowSeverityMenu(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        value === "critical" ? "#dc2626" : "#f59e0b";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        value === alarmSeverityFilter ? "#374151" : "#1f2933";
                    }}
                    style={{
                      padding: "9px 14px",
                      cursor: "pointer",
                      color: "#ffffff",
                      background:
                        value === alarmSeverityFilter ? "#374151" : "#1f2933",
                      fontWeight: value === alarmSeverityFilter ? 700 : 500,
                    }}
                  >
                    {value === "critical" ? "Critical" : "Warning"}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Channel tabs. Every tab's data is prefetched on open, so switching is
          instant; a tab stays disabled only until that prefetch resolves. */}
      <div className="chart-popout-tabs-nav">
        <button
          type="button"
          className="chart-popout-tab-scroll-button"
          onClick={() => scrollChannelTabs(-1)}
          disabled={!canScrollTabsLeft}
          aria-label="Scroll channel tabs left"
          title="Scroll channel tabs left"
        >
          ‹
        </button>

        <div
          className="chart-popout-tabs"
          ref={channelTabsRef}
          onScroll={updateChannelTabScrollState}
        >
          {channelTabs.map((tab) => {
            const isPjr = tab.value === PJR_TAB_VALUE;
            const isChemRate = tab.value === CHEM_RATE_TAB_VALUE;
            const isChemRatio = tab.value === CHEM_RATIO_TAB_VALUE;
            const isNetPressure = tab.value === NET_PRESSURE_TAB_VALUE;
            const isSpecial =
              isPjr || isChemRate || isChemRatio || isNetPressure;
            const isActive = tab.value === activeChannelValue;
            const tabHasData = isPjr
              ? pjrHasData
              : isChemRate
                ? chemicalRateHasData
                : isChemRatio
                  ? chemicalRatioHasData
                  : isNetPressure
                    ? netPressureHasData
                    : channelHasData(tab.value);
            const tabPrefetchComplete =
              isPjr || isNetPressure
                ? pjrPrefetchComplete
                : isChemRate || isChemRatio
                  ? chemicalPrefetchComplete
                  : prefetchComplete;
            const isDisabled = !tabPrefetchComplete && !tabHasData;

            return (
              <button
                key={tab.value}
                type="button"
                aria-pressed={isActive}
                disabled={isDisabled}
                onClick={() => selectChannelTab(tab.value)}
                title={
                  isDisabled
                    ? isSpecial
                      ? `Loading ${tab.label}…`
                      : "Loading this channel…"
                    : tabHasData
                      ? tab.label
                      : `${tab.label} — no data in this time range`
                }
                style={{
                  flex: "0 0 auto",
                  height: 30,
                  padding: "0 12px",
                  borderRadius: 6,
                  border: isActive ? "1px solid #8cc540" : "1px solid #4b5563",
                  background: isActive ? "#31451f" : "#2d3748",
                  color: isDisabled
                    ? "#8b949e"
                    : tabHasData || !tabPrefetchComplete
                      ? "#ffffff"
                      : "#9ca3af",
                  fontSize: 12,
                  fontWeight: isActive ? 800 : 600,
                  whiteSpace: "nowrap",
                  cursor: isDisabled ? "progress" : "pointer",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                {tab.label}
              </button>
            );
          })}

          {!prefetchComplete && (
            <span
              style={{
                flex: "0 0 auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                marginLeft: 4,
                color: "#c7cdd3",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 13,
                  height: 13,
                  border: "2px solid #5b6168",
                  borderTopColor: "#8cc540",
                  borderRadius: "50%",
                  animation: "chartPopoutSpin 0.8s linear infinite",
                }}
              />
              Loading all channels…
            </span>
          )}

          {prefetchComplete &&
            params.mode === "fleet" &&
            (!pjrPrefetchComplete || !chemicalPrefetchComplete) && (
              <span
                style={{
                  flex: "0 0 auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  marginLeft: 4,
                  color: "#c7cdd3",
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 13,
                    height: 13,
                    border: "2px solid #5b6168",
                    borderTopColor: "#8cc540",
                    borderRadius: "50%",
                    animation: "chartPopoutSpin 0.8s linear infinite",
                  }}
                />
                {!pjrPrefetchComplete && !chemicalPrefetchComplete
                  ? "Loading PJR and chemicals…"
                  : !pjrPrefetchComplete
                    ? "Loading PJR…"
                    : "Loading chemicals…"}
              </span>
            )}

          {prefetchComplete && prefetchError && (
            <span
              style={{
                flex: "0 0 auto",
                marginLeft: 4,
                color: "#ffd7d7",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              ⚠ {prefetchError}
            </span>
          )}
        </div>

        <button
          type="button"
          className="chart-popout-tab-scroll-button"
          onClick={() => scrollChannelTabs(1)}
          disabled={!canScrollTabsRight}
          aria-label="Scroll channel tabs right"
          title="Scroll channel tabs right"
        >
          ›
        </button>
      </div>

      <div className="chart-popout-chart" ref={chartAreaRef}>
        {hasChartData && chartSize.width > 0 && (
          <HighchartsReact
            key={
              isSpecialTab
                ? `special-${activeChannelValue}`
                : `pump-${selectedChannelValues.join("|")}`
            }
            highcharts={Highcharts}
            options={chartOptions}
            callback={handleChartCreated}
          />
        )}

        {(isTabLoading || !hasChartData) && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              boxSizing: "border-box",
              color: "#f8fafc",
              background: hasChartData ? "rgba(48, 51, 53, 0.82)" : "#303335",
            }}
          >
            <div
              style={{
                width: "min(520px, 92vw)",
                padding: 28,
                border: "1px solid #5b6168",
                borderRadius: 12,
                background: "#262b2f",
                boxShadow: "0 16px 45px rgba(0,0,0,0.35)",
                textAlign: "center",
              }}
            >
              {isTabLoading ? (
                <>
                  <div
                    aria-hidden="true"
                    style={{
                      width: 34,
                      height: 34,
                      margin: "0 auto 16px",
                      border: "4px solid #5b6168",
                      borderTopColor: "#8cc540",
                      borderRadius: "50%",
                      animation: "chartPopoutSpin 0.8s linear infinite",
                    }}
                  />
                  <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>
                    Loading {activeChannelLabel}
                  </h2>
                  <p style={{ margin: 0, color: "#c7cdd3", lineHeight: 1.5 }}>
                    {params.mode === "asset"
                      ? "Retrieving data for the selected assets. You can remain on this window while the channel loads."
                      : isPjrTab
                        ? `Retrieving PJR treatment channels for Fleet ${params.fleet}.`
                        : isChemicalTab
                          ? `Retrieving chemical channels for Fleet ${params.fleet}.`
                          : isNetPressureTab
                            ? `Retrieving net pressure for Fleet ${params.fleet}.`
                            : `Retrieving data for Fleet ${params.fleet}. You can remain on this window while the channel loads.`}
                  </p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>ⓘ</div>
                  <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>
                    {tabError
                      ? "Unable to load chart data"
                      : `No data available for ${activeChannelLabel}`}
                  </h2>
                  <p
                    style={{
                      margin: "0 0 20px",
                      color: "#c7cdd3",
                      lineHeight: 1.5,
                    }}
                  >
                    {tabError
                      ? tabError
                      : isPjrTab
                        ? "No PJR treatment readings for the selected fleet, wells, and time range. Pick another channel tab above."
                        : isChemicalTab
                          ? "No chemical readings for the selected fleet, wells, and time range. Pick another channel tab above."
                          : isNetPressureTab
                            ? "No net pressure readings for the selected fleet, wells, and time range. Pick another channel tab above."
                            : params.mode === "asset"
                              ? "This channel has no readings for the selected assets and time range. Pick another channel tab above."
                              : "This channel has no readings for the selected fleet, pumps, wells, and time range. Pick another channel tab above."}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (isPjrTab || isNetPressureTab) {
                          setPjrError("");
                          setPjrLoading(true);
                          setPjrPrefetchComplete(false);
                          setPjrReloadRequest((previous) => previous + 1);
                          return;
                        }
                        if (isChemicalTab) {
                          setChemicalError("");
                          setChemicalLoading(true);
                          setChemicalPrefetchComplete(false);
                          setChemicalReloadRequest((previous) => previous + 1);
                          return;
                        }
                        setError("");
                        setLoading(true);
                        setReloadRequest((previous) => previous + 1);
                      }}
                      style={{
                        height: 40,
                        padding: "0 16px",
                        borderRadius: 7,
                        border: "1px solid #6b7280",
                        background: "#374151",
                        color: "#ffffff",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Retry
                    </button>
                    {activeChannelValue !== (params.channels[0] || "rpm") && (
                      <button
                        type="button"
                        onClick={() =>
                          selectChannelTab(params.channels[0] || "rpm")
                        }
                        style={{
                          height: 40,
                          padding: "0 16px",
                          borderRadius: 7,
                          border: "1px solid #8cc540",
                          background: "#31451f",
                          color: "#ffffff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Back to original channel
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={returnToPumpData}
                      style={{
                        height: 40,
                        padding: "0 18px",
                        borderRadius: 7,
                        border: "none",
                        background: "#8cc540",
                        color: "#ffffff",
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Back to Pump Data
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes chartPopoutSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {axisValueEdit && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.56)",
            backdropFilter: "blur(1px)",
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeAxisValueEditor();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "min(460px, 92vw)",
              borderRadius: 14,
              border: "1px solid #5C646B",
              background: "#30353b",
              color: "#ffffff",
              boxShadow: "0 22px 60px rgba(0,0,0,0.5)",
              overflow: "hidden",
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid #4b5563",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 18 }}>Edit Axis Scale</h3>
                <div style={{ marginTop: 4, color: "#b7c0ca", fontSize: 13 }}>
                  {axisValueEdit.axisTitle}
                </div>
              </div>
              <button
                type="button"
                onClick={closeAxisValueEditor}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  background: "transparent",
                  color: "#ffffff",
                  fontSize: 24,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                aria-label="Close axis scale modal"
              >
                ×
              </button>
            </div>

            <div style={{ padding: 18 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    border: "1px solid #4b5563",
                    borderRadius: 10,
                    padding: 10,
                    background: "#262b31",
                  }}
                >
                  <div style={{ color: "#9ca3af", fontSize: 12 }}>
                    Clicked value
                  </div>
                  <strong>
                    {Number(axisValueEdit.clickedValue.toFixed(2))}
                  </strong>
                </div>
                <div
                  style={{
                    border: "1px solid #4b5563",
                    borderRadius: 10,
                    padding: 10,
                    background: "#262b31",
                  }}
                >
                  <div style={{ color: "#9ca3af", fontSize: 12 }}>
                    Current range
                  </div>
                  <strong>
                    {Number(axisValueEdit.currentMin.toFixed(2))} –{" "}
                    {Number(axisValueEdit.currentMax.toFixed(2))}
                  </strong>
                </div>
              </div>

              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                New value
              </label>
              <input
                type="number"
                value={axisValueInput}
                onChange={(event) => {
                  setAxisValueInput(event.target.value);
                  setAxisValueEdit((prev) =>
                    prev ? { ...prev, error: undefined } : prev,
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyAxisValueEdit();
                  if (event.key === "Escape") closeAxisValueEditor();
                }}
                autoFocus
                onMouseDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: "100%",
                  height: 42,
                  borderRadius: 10,
                  border: axisValueEdit.error
                    ? "1px solid #ff8a8a"
                    : "1px solid #5C646B",
                  background: "#ffffff",
                  color: "#111827",
                  padding: "0 12px",
                  fontSize: 15,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />

              {axisValueEdit.error && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "rgba(255, 80, 80, 0.16)",
                    border: "1px solid rgba(255, 120, 120, 0.42)",
                    color: "#ffdede",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  ⚠ {axisValueEdit.error}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 18px 16px",
                borderTop: "1px solid #4b5563",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={closeAxisValueEditor}
                style={{
                  height: 38,
                  padding: "0 14px",
                  borderRadius: 9,
                  border: "1px solid #5C646B",
                  background: "#3a4048",
                  color: "#ffffff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={resetAxisValueToAuto}
                style={{
                  height: 38,
                  padding: "0 14px",
                  borderRadius: 9,
                  border: "1px solid #5C646B",
                  background: "#3a4048",
                  color: "#ffffff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Reset Auto
              </button>
              <button
                type="button"
                onClick={applyAxisValueEdit}
                style={{
                  height: 38,
                  padding: "0 16px",
                  borderRadius: 9,
                  border: "none",
                  background: "#8cc540",
                  color: "#ffffff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlarmModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowAlarmModal(false)}
        >
          <div
            style={{
              width: "min(720px, 92vw)",
              maxHeight: "70vh",
              overflow: "auto",
              background: "#303335",
              color: "#ffffff",
              border: "1px solid #5C646B",
              borderRadius: 10,
              padding: 16,
              boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>Alarm Details</h3>
              <button
                type="button"
                onClick={() => setShowAlarmModal(false)}
                style={{
                  background: "transparent",
                  color: "#ffffff",
                  border: "1px solid #5C646B",
                  borderRadius: 6,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            {selectedAlarmEvents.map((alarm, index) => {
              const severity = normalizeAlarmSeverity(alarm);
              const meta = getAlarmSeverityMeta(severity);
              const alarmTime =
                alarm.alarm_time || alarm.time_stamp || alarm.timestamp;

              return (
                <div
                  key={`${alarm.pump}-${alarmTime}-${index}`}
                  style={{
                    borderTop: index === 0 ? "none" : "1px solid #5C646B",
                    padding: "10px 0",
                  }}
                >
                  <div style={{ color: meta.color, fontWeight: 700 }}>
                    {meta.marker} {meta.label}
                  </div>
                  <div>
                    Pump: {alarm.pump || alarm.source || alarm.pump_name || "-"}
                  </div>
                  <div>Alarm: {alarm.alarm || alarm.name || "-"}</div>
                  <div>
                    Time:{" "}
                    {alarmTime ? new Date(alarmTime).toLocaleString() : "-"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartPopout;
